/**
 * Global admin page for editing equipment, exercises, warmups, finishers.
 * Every item: editable inputs with Edit → Cancel / Save.
 * Warmups & Finishers: show exercises and sets as editable rows.
 * Equipment: photo upload → Firebase Storage content/equipment/{id}.{ext} → photo_url.
 * Exercises: video upload → Firebase Storage content/exercises/{id}/video.{ext} → media.video.
 *
 * Rule of thumb: If you edit it here and want changes to persist, it must live in Firestore.
 * - Equipment, exercises, warmups, finishers: load from DB when tab active; save writes to Firestore.
 * - Firestore is the source of truth; bundled JSON is only a loading placeholder until the
 *   DB fetch resolves. `npm run backup:content` exports Firestore back to JSON. Add new types
 *   by adding a tab,
 *   a Firestore collection + get/update helpers, and the same fetch-on-tab / save-on-edit pattern.
 */
import React, { useState, useRef, useEffect, useCallback, useMemo, Fragment } from 'react';
import { useLocation } from 'react-router-dom';
import { storage, auth } from '../../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import {
  getEquipment,
  updateEquipment,
  deleteEquipment,
  cascadeRemoveEquipmentFromTiers,
  getEquipmentTiers,
  updateEquipmentTier,
  getExercises,
  updateExercise,
  updateExerciseVideoUrl,
  deleteExercise,
  cascadeRemoveExerciseFromReferences,
  getWarmupTemplates,
  updateWarmupTemplate,
  deleteWarmupTemplate,
  getFinisherTemplates,
  updateFinisherTemplate,
  deleteFinisherTemplate,
  getDrillTemplates,
  updateDrillTemplate,
  getWorkoutTemplates,
  getProgramList,
} from '../../utils/adminData';
import AdminWorkoutEditorModal, { WORKOUT_PROGRAM_OPTIONS as WORKOUT_EDITOR_PROGRAM_OPTIONS } from '../../components/AdminWorkoutEditorModal';
import equipmentList from '../../data/core/equipment.json';
import equipmentTiersJson from '../../data/core/equipment_tiers.json';

import warmupTemplatesList from '../../data/core/warmup_templates.json';
import finisherTemplatesList from '../../data/core/finisher_templates.json';
import drillTemplatesList from '../../data/core/drill_templates.json';
import workoutTemplatesList from '../../data/core/workout_templates.json';
import EditIcon from '../../assets/icons/edit-square.svg';
import '../../styles/Admin.css';

/* Sidenav only — no in-page tabs. Section title + Add button live in each catalog. */

function ContentEditModal({ title, onClose, mediaSection, body, footer, modalClassName }) {
  useEffect(() => {
    const handleEscape = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  return (
    <div
      className="admin-content-edit-modal-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="content-edit-modal-title"
    >
      <div className={`admin-content-edit-modal ${modalClassName || ''}`.trim()} onClick={(e) => e.stopPropagation()}>
        <header className="admin-content-edit-modal-header">
          <h2 id="content-edit-modal-title" className="admin-content-edit-modal-title">{title}</h2>
          <button type="button" className="admin-content-edit-modal-close" onClick={onClose} aria-label="Close">×</button>
        </header>
        {mediaSection && mediaSection}
        <div className="admin-content-edit-modal-body">{body}</div>
        <footer className="admin-content-edit-modal-footer">{footer}</footer>
      </div>
    </div>
  );
}

function getContentTabFromPath(pathname) {
  const match = pathname.match(/^\/admin\/content\/(equipment|exercises|warmups|finishers|drills|workouts)$/);
  return match ? match[1] : 'equipment';
}

function AdminContent() {
  const location = useLocation();
  const pathname = location.pathname ?? '';
  const activeTab = getContentTabFromPath(pathname);

  const [equipmentListFromFirestore, setEquipmentListFromFirestore] = useState([]);
  const [equipmentTiersFromFirestore, setEquipmentTiersFromFirestore] = useState([]);
  const [exercisesListFromFirestore, setExercisesListFromFirestore] = useState([]);
  const [warmupsListFromFirestore, setWarmupsListFromFirestore] = useState([]);
  const [finishersListFromFirestore, setFinishersListFromFirestore] = useState([]);
  const [drillsListFromFirestore, setDrillsListFromFirestore] = useState([]);
  const [workoutsListFromFirestore, setWorkoutsListFromFirestore] = useState([]);
  const [equipmentFetched, setEquipmentFetched] = useState(false);
  const [tiersFetched, setTiersFetched] = useState(false);
  const [exercisesFetched, setExercisesFetched] = useState(false);
  const [warmupsFetched, setWarmupsFetched] = useState(false);
  const [finishersFetched, setFinishersFetched] = useState(false);
  const [drillsFetched, setDrillsFetched] = useState(false);
  const [workoutsFetched, setWorkoutsFetched] = useState(false);
  const [programsList, setProgramsList] = useState([]);
  const fetchOnTab = useCallback((tab, setFetched, setData, getFn, jsonFallback) => {
    const tabs = Array.isArray(tab) ? tab : [tab];
    if (!tabs.includes(activeTab)) return;
    setFetched(false);
    getFn()
      .then((data) => {
        setData(data || []);
        setFetched(true);
      })
      .catch(() => {
        setData(jsonFallback || []);
        setFetched(true);
      });
  }, [activeTab]);

  useEffect(() => {
    fetchOnTab('equipment', setEquipmentFetched, setEquipmentListFromFirestore, getEquipment, equipmentList);
  }, [activeTab, fetchOnTab]);

  useEffect(() => {
    if (activeTab !== 'equipment') return;
    setTiersFetched(false);
    getEquipmentTiers()
      .then((data) => { setEquipmentTiersFromFirestore(data || []); setTiersFetched(true); })
      .catch(() => { setEquipmentTiersFromFirestore([]); setTiersFetched(true); });
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== 'exercises' && activeTab !== 'warmups' && activeTab !== 'drills' && activeTab !== 'workouts') return;
    setExercisesFetched(false);
    getExercises()
      .then((data) => {
        setExercisesListFromFirestore(data || []);
        setExercisesFetched(true);
      })
      .catch((err) => {
        console.error('[AdminContent] Failed to fetch exercises from Firestore:', err);
        setExercisesListFromFirestore([]);
        setExercisesFetched(true);
      });
  }, [activeTab]);

  // Program list drives the Program dropdowns on the Warmups / Finishers / Workouts
  // tabs, so any program authored/published in the CMS shows up automatically
  // (no hardcoded program list). Fetched once when a program-scoped tab is opened.
  useEffect(() => {
    if (activeTab !== 'warmups' && activeTab !== 'finishers' && activeTab !== 'workouts') return;
    getProgramList()
      .then((list) => setProgramsList(Array.isArray(list) ? list : []))
      .catch(() => setProgramsList([]));
  }, [activeTab]);

  // Build { value, label } options from the DB program list, de-duplicated by id.
  // Filter dropdowns get a leading "All" and trailing "Shared"; the editor selects
  // strip "All" (see each catalog) but keep "Shared".
  const programOptions = useMemo(() => {
    const seen = new Set();
    const programs = programsList
      .map((p) => ({ value: p.id, label: p.displayName || p.name || p.id }))
      .filter((o) => o.value && !seen.has(o.value) && seen.add(o.value));
    return [{ value: '', label: 'All' }, ...programs, { value: 'shared', label: 'Shared' }];
  }, [programsList]);

  useEffect(() => {
    fetchOnTab(['warmups', 'workouts'], setWarmupsFetched, setWarmupsListFromFirestore, getWarmupTemplates, warmupTemplatesList);
  }, [activeTab, fetchOnTab]);

  useEffect(() => {
    fetchOnTab(['finishers', 'workouts'], setFinishersFetched, setFinishersListFromFirestore, getFinisherTemplates, finisherTemplatesList);
  }, [activeTab, fetchOnTab]);

  useEffect(() => {
    fetchOnTab('drills', setDrillsFetched, setDrillsListFromFirestore, getDrillTemplates, drillTemplatesList);
  }, [activeTab, fetchOnTab]);

  useEffect(() => {
    if (activeTab !== 'workouts') return;
    setWorkoutsFetched(false);
    getWorkoutTemplates()
      .then((firestoreList) => {
        const byId = new Map();
        (workoutTemplatesList || []).forEach((t) => byId.set(t.id, { ...t }));
        (firestoreList || []).forEach((t) => byId.set(t.id, { id: t.id, ...t }));
        setWorkoutsListFromFirestore(Array.from(byId.values()));
        setWorkoutsFetched(true);
      })
      .catch(() => {
        setWorkoutsListFromFirestore(workoutTemplatesList || []);
        setWorkoutsFetched(true);
      });
  }, [activeTab]);

  // Use Firestore data once fetched. JSON only on fetch error.
  const equipmentListToShow = equipmentFetched ? equipmentListFromFirestore : [];
  const exercisesListToShow = exercisesFetched ? exercisesListFromFirestore : [];
  const warmupsListToShow = warmupsFetched ? warmupsListFromFirestore : [];
  const finishersListToShow = finishersFetched ? finishersListFromFirestore : [];
  const drillsListToShow = drillsFetched ? drillsListFromFirestore : [];
  const workoutsListToShow = workoutsFetched ? workoutsListFromFirestore : [];
  const equipmentLoading = activeTab === 'equipment' && !equipmentFetched;
  const exercisesLoading = activeTab === 'exercises' && !exercisesFetched;
  const warmupsLoading = activeTab === 'warmups' && !warmupsFetched;
  const finishersLoading = activeTab === 'finishers' && !finishersFetched;
  const drillsLoading = activeTab === 'drills' && !drillsFetched;
  const workoutsLoading = activeTab === 'workouts' && !workoutsFetched;
  const exercisesListForSelect = exercisesFetched ? exercisesListFromFirestore : [];
  const exercisesListForWarmup = useMemo(() => {
    const list = exercisesFetched ? exercisesListFromFirestore : [];
    return (list || []).filter((e) => {
      const tags = Array.isArray(e.tags)
        ? e.tags
        : (typeof e.tags === 'string' ? e.tags.split(',').map((s) => s.trim()).filter(Boolean) : []);
      return tags.includes('warmup') || (e.category || '').toLowerCase() === 'mobility';
    }).sort((a, b) => (String(a?.name ?? '').toLowerCase()).localeCompare(String(b?.name ?? '').toLowerCase()));
  }, [exercisesFetched, exercisesListFromFirestore]);
  const warmupsListForSelect = warmupsFetched ? warmupsListFromFirestore : (warmupTemplatesList || []);
  const finishersListForSelect = finishersFetched ? finishersListFromFirestore : (finisherTemplatesList || []);

  const exercisesListForDrills = useMemo(() => {
    const list = exercisesFetched ? exercisesListFromFirestore : [];
    return (list || []).filter((e) => {
      const tags = Array.isArray(e.tags)
        ? e.tags
        : (typeof e.tags === 'string' ? e.tags.split(',').map((s) => s.trim()).filter(Boolean) : []);
      return tags.some((t) => String(t).toLowerCase() === 'drills');
    }).sort((a, b) => (String(a?.name ?? '').toLowerCase()).localeCompare(String(b?.name ?? '').toLowerCase()));
  }, [exercisesFetched, exercisesListFromFirestore]);

  const byName = (a, b) => (String(a?.name ?? '').toLowerCase()).localeCompare(String(b?.name ?? '').toLowerCase());
  const equipmentSorted = [...equipmentListToShow].sort(byName);

  const byMuscleCategoryName = (a, b) => {
    const muscleA = (a?.muscles_primary?.[0] ?? '_other').toLowerCase();
    const muscleB = (b?.muscles_primary?.[0] ?? '_other').toLowerCase();
    if (muscleA !== muscleB) return muscleA.localeCompare(muscleB);
    const catA = String(a?.category ?? '').toLowerCase();
    const catB = String(b?.category ?? '').toLowerCase();
    if (catA !== catB) return catA.localeCompare(catB);
    return String(a?.name ?? '').toLowerCase().localeCompare(String(b?.name ?? '').toLowerCase());
  };
  const exercisesSorted = [...exercisesListToShow].sort(byMuscleCategoryName);

  return (
    <div className="admin-content">
      <div className="admin-content-panel">
        {equipmentLoading && (
          <div className="admin-content-loading">
            <p className="text-secondary">Loading equipment from database…</p>
          </div>
        )}
        {activeTab === 'equipment' && !equipmentLoading && (
          <>
            <AdminEquipmentTiers
              tiersFromDb={equipmentTiersFromFirestore}
              tiersFetched={tiersFetched}
              equipmentCatalog={equipmentListToShow}
              onAfterSave={() => getEquipmentTiers().then((data) => { setEquipmentTiersFromFirestore(data || []); setTiersFetched(true); })}
            />
            <AdminEquipmentCatalog
              list={equipmentSorted}
              onAfterSave={() => getEquipment().then((data) => { setEquipmentListFromFirestore(data || []); })}
            />
          </>
        )}
        {exercisesLoading && (
          <div className="admin-content-loading">
            <p className="text-secondary">Loading exercises from database…</p>
          </div>
        )}
        {activeTab === 'exercises' && !exercisesLoading && (
          <AdminExercisesCatalog
            list={exercisesSorted}
            onAfterSave={() => getExercises().then((data) => { setExercisesListFromFirestore(data || []); })}
          />
        )}
        {warmupsLoading && (
          <div className="admin-content-loading">
            <p className="text-secondary">Loading warmups from database…</p>
          </div>
        )}
        {activeTab === 'warmups' && !warmupsLoading && (
          <AdminWarmupsCatalog
            list={warmupsListToShow}
            exercisesList={exercisesListForWarmup}
            exercisesListForLookup={exercisesListForSelect}
            programOptions={programOptions}
            onAfterSave={() => getWarmupTemplates().then((data) => { setWarmupsListFromFirestore(data || []); })}
          />
        )}
        {finishersLoading && (
          <div className="admin-content-loading">
            <p className="text-secondary">Loading finishers from database…</p>
          </div>
        )}
        {activeTab === 'finishers' && !finishersLoading && (
          <AdminFinishersCatalog
            list={finishersListToShow}
            exercisesList={exercisesListForSelect}
            programOptions={programOptions}
            onAfterSave={() => getFinisherTemplates().then((data) => { setFinishersListFromFirestore(data || []); })}
          />
        )}
        {drillsLoading && (
          <div className="admin-content-loading">
            <p className="text-secondary">Loading drills from database…</p>
          </div>
        )}
        {activeTab === 'drills' && !drillsLoading && (
          <AdminDrillsCatalog
            list={drillsListToShow}
            exercisesList={exercisesListForDrills}
            exercisesListForLookup={exercisesListForSelect}
            onAfterSave={() => getDrillTemplates().then((data) => { setDrillsListFromFirestore(data || []); })}
          />
        )}
        {workoutsLoading && (
          <div className="admin-content-loading">
            <p className="text-secondary">Loading workouts from database…</p>
          </div>
        )}
        {activeTab === 'workouts' && !workoutsLoading && (
          <AdminWorkoutsCatalog
            list={workoutsListToShow}
            warmupsList={warmupsListForSelect}
            finishersList={finishersListForSelect}
            exercisesList={exercisesListForSelect}
            programOptions={programOptions}
            onAfterSave={() => getWorkoutTemplates().then((data) => { setWorkoutsListFromFirestore(data || []); })}
          />
        )}
      </div>

    </div>
  );
}

// Slug for new doc ids: lowercase, underscores, no spaces
function toSlug(s) {
  return String(s || '').trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
}

const EQUIPMENT_CATEGORIES = [
  { value: '', label: '—' },
  { value: 'free_weights', label: 'Free weights' },
  { value: 'furniture', label: 'Furniture' },
  { value: 'functional', label: 'Functional' },
  { value: 'accessories', label: 'Accessories' },
  { value: 'resistance_band', label: 'Resistance band' },
  { value: 'recovery', label: 'Recovery' },
  { value: 'facility', label: 'Facility' },
];
const RESISTANCE_LEVELS = [
  { value: 'light', label: 'Light' },
  { value: 'medium', label: 'Medium' },
  { value: 'heavy', label: 'Heavy' },
];

const EMPTY_EQUIPMENT = { id: '', name: '', category: '', manufacturer: '', description: '', tags: '', weights_lb: [], resistance_levels: [], photo_url: '', product_url: '', product_retailer: '' };
const EMPTY_EXERCISE = { id: '', name: '', difficulty: '', category: '', movement_pattern: '', muscles_primary: '', equipment: '', description: '', tags: [], media: { video: '' } };
const EXERCISE_ROLE_TAGS = [
  { value: 'warmup', label: 'Warmup' },
  { value: 'finisher', label: 'Finisher' },
  { value: 'drills', label: 'Drills' },
];
const EMPTY_WARMUP = { id: '', name: '', duration_minutes: 8, program: null, exercises: [] };

const WARMUP_PROGRAM_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'volleyball', label: 'Volleyball' },
  { value: 'multi_sport_sprint', label: 'Multi-Sport Sprint' },
  { value: 'shared', label: 'Shared' },
];
const FINISHER_PROGRAM_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'volleyball', label: 'Volleyball' },
  { value: 'multi_sport_sprint', label: 'Multi-Sport Sprint' },
  { value: 'shared', label: 'Shared' },
];

function getFinisherProgram(item) {
  if (item?.program != null && item.program !== '') return item.program;
  const id = (item?.id || '').toLowerCase();
  if (id.startsWith('vb_') || id.includes('volleyball')) return 'volleyball';
  if (id.startsWith('multi_sport_') || id.includes('multi_sport')) return 'multi_sport_sprint';
  return null; // shared
}

const EMPTY_FINISHER = { id: '', name: '', duration_minutes: 10, program: null, equipment_required: [], exercises: [] };
const EMPTY_DRILL = { id: '', name: '', duration_minutes: 15, description: '', warmup_exercises: [], exercises: [], finisher_exercises: [] };

function isYouTubeUrl(url) {
  return !!(
    url &&
    typeof url === 'string' &&
    (url.includes('youtu.be/') || url.includes('youtube.com/watch') || url.includes('youtube.com/embed/'))
  );
}

function getYouTubeVideoId(url) {
  if (!url || typeof url !== 'string') return null;
  if (url.includes('youtu.be/')) return url.split('youtu.be/')[1]?.split(/[?&]/)[0] ?? null;
  if (url.includes('youtube.com/watch')) {
    try { return new URL(url).searchParams.get('v'); } catch { return url.split('v=')[1]?.split('&')[0] ?? null; }
  }
  if (url.includes('youtube.com/embed/')) return url.split('youtube.com/embed/')[1]?.split(/[?&]/)[0] ?? null;
  return null;
}

function getYouTubeEmbedUrl(url) {
  const id = getYouTubeVideoId(url);
  return id ? `https://www.youtube.com/embed/${id}` : null;
}

// ---- Equipment Tiers ----
function AdminEquipmentTiers({ tiersFromDb, tiersFetched, equipmentCatalog, onAfterSave }) {
  const [editingTier, setEditingTier] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Merge: JSON is the source, DB overrides if present
  const mergedTiers = useMemo(() => {
    const dbById = {};
    (tiersFromDb || []).forEach((t) => { dbById[t.id] = t; });
    return equipmentTiersJson.map((jsonTier) => dbById[jsonTier.id] || jsonTier);
  }, [tiersFromDb]);

  const equipmentNameById = useMemo(() => {
    const map = {};
    (equipmentCatalog || []).forEach((e) => { map[e.id] = e.name || e.id; });
    // Also fill from JSON fallback
    equipmentList.forEach((e) => { if (!map[e.id]) map[e.id] = e.name || e.id; });
    return map;
  }, [equipmentCatalog]);


  const openEdit = (tier) => {
    setSaveError(null);
    setEditingTier(tier);
    setDraft({
      ...tier,
      facility_areas: (tier.facility_areas || []).map((a) => ({
        ...a,
        equipment: [...(a.equipment || [])],
      })),
      overrides: { ...(tier.overrides || {}) },
    });
  };
  const closeModal = () => { setEditingTier(null); setDraft(null); setSaveError(null); };

  const toggleEquipment = (areaIndex, eqId) => {
    setDraft((d) => {
      const areas = d.facility_areas.map((a, i) => {
        if (i !== areaIndex) return a;
        const current = [...(a.equipment || [])];
        const idx = current.indexOf(eqId);
        if (idx >= 0) current.splice(idx, 1);
        else current.push(eqId);
        return { ...a, equipment: current };
      });
      return { ...d, facility_areas: areas };
    });
  };

  const updateOverride = (eqId, field, value) => {
    setDraft((d) => {
      const overrides = { ...(d.overrides || {}) };
      if (!overrides[eqId]) overrides[eqId] = {};
      overrides[eqId] = { ...overrides[eqId], [field]: value };
      // Remove empty override
      if (Object.values(overrides[eqId]).every((v) => v === '' || v === null || (Array.isArray(v) && v.length === 0))) {
        delete overrides[eqId];
      }
      return { ...d, overrides };
    });
  };

  const saveEdit = async () => {
    if (!draft?.id) return;
    setSaving(true);
    setSaveError(null);
    try {
      await updateEquipmentTier(draft.id, draft);
      if (typeof onAfterSave === 'function') await onAfterSave();
      closeModal();
    } catch (err) {
      console.error('[AdminContent] Equipment tier save failed:', err);
      setSaveError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const dbTierIds = (tiersFromDb || []).map((t) => t.id);

  return (
    <div className="admin-equipment-tiers">
      <div className="admin-content-top">
        <div className="admin-content-top-heading">
          <h2 className="admin-content-section-title">Equipment Tiers</h2>
          <p className="admin-content-section-desc">
            Three equipment configurations for different training environments.
          </p>
        </div>
      </div>

      {!tiersFetched && (
        <p className="text-secondary" style={{ padding: 'var(--space-3)' }}>Loading tiers from database…</p>
      )}

      <div className="admin-tiers-grid">
        {mergedTiers.map((tier) => {
          const inDb = dbTierIds.includes(tier.id);
          return (
            <div key={tier.id} className="admin-tier-card">
              <div className="admin-tier-card-header">
                <div>
                  <strong className="admin-tier-card-name">{tier.name}</strong>
                  <span className={`admin-tier-badge ${inDb ? 'admin-tier-badge--synced' : 'admin-tier-badge--local'}`}>
                    {inDb ? 'In DB' : 'JSON only'}
                  </span>
                </div>
                <button type="button" className="admin-catalog-edit-btn" onClick={() => openEdit(tier)} aria-label="Edit">
                  <img src={EditIcon} alt="" className="admin-catalog-edit-icon" />
                </button>
              </div>
              <p className="admin-tier-card-desc text-secondary text-sm">{tier.description}</p>
              {(tier.facility_areas || []).map((area) => (
                <div key={area.area_id} className="admin-tier-area">
                  <strong className="admin-tier-area-name">{area.area_name}</strong>
                  <div className="admin-tier-equipment-chips">
                    {(area.equipment || []).map((eqId) => {
                      const override = tier.overrides?.[eqId];
                      return (
                        <span key={eqId} className="admin-tier-chip" title={override ? `Override: ${JSON.stringify(override)}` : ''}>
                          {equipmentNameById[eqId] || eqId}
                          {override && <span className="admin-tier-chip-override">*</span>}
                        </span>
                      );
                    })}
                  </div>
                </div>
              ))}
              {tier.overrides && Object.keys(tier.overrides).length > 0 && (
                <div className="admin-tier-overrides">
                  <strong className="text-sm">Overrides:</strong>
                  {Object.entries(tier.overrides).map(([eqId, ov]) => (
                    <div key={eqId} className="admin-tier-override-row text-sm text-secondary">
                      <span className="admin-tier-override-label">{equipmentNameById[eqId] || eqId}:</span>
                      {ov.weights_lb && <span>weights {ov.weights_lb.join(', ')} lb</span>}
                      {ov.resistance_levels && <span>levels {ov.resistance_levels.join(', ')}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {editingTier && draft && (
        <ContentEditModal
          title={`Edit: ${editingTier.name}`}
          onClose={closeModal}
          body={
            <div className="admin-tier-edit">
              <div className="admin-catalog-edit-fields">
                <div className="admin-catalog-field">
                  <label>ID</label>
                  <input type="text" className="input" value={draft.id || ''} readOnly />
                </div>
                <div className="admin-catalog-field">
                  <label>Name</label>
                  <input type="text" className="input" value={draft.name || ''} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} />
                </div>
                <div className="admin-catalog-field admin-catalog-field--wide">
                  <label>Description</label>
                  <textarea className="input" rows={2} value={draft.description || ''} onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))} />
                </div>
              </div>

              {(draft.facility_areas || []).map((area, ai) => (
                <div key={area.area_id} className="admin-tier-edit-area">
                  <h4 className="admin-tier-edit-area-title">{area.area_name}</h4>
                  <div className="admin-tier-equipment-checkboxes">
                    {(equipmentCatalog || []).map((eq) => {
                      const checked = (area.equipment || []).includes(eq.id);
                      return (
                        <label key={eq.id} className="admin-tier-eq-checkbox">
                          <input type="checkbox" checked={checked} onChange={() => toggleEquipment(ai, eq.id)} />
                          <span>{eq.name || eq.id}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}

              <div className="admin-tier-edit-overrides">
                <h4 className="admin-tier-edit-area-title">Weight / Level Overrides</h4>
                <p className="text-sm text-secondary" style={{ marginBottom: 'var(--space-2)' }}>
                  Override default weights or resistance levels for this tier. Leave blank to use catalog defaults.
                </p>
                {(() => {
                  const allEqIds = new Set();
                  (draft.facility_areas || []).forEach((a) => (a.equipment || []).forEach((id) => allEqIds.add(id)));
                  const catalog = equipmentCatalog || [];
                  const hasWeightsOrLevels = (e) => e.has_weights || (Array.isArray(e.weights_lb) && e.weights_lb.length > 0) || (Array.isArray(e.resistance_levels) && e.resistance_levels.length > 0);
                  const weightItems = catalog.filter((e) => allEqIds.has(e.id) && hasWeightsOrLevels(e));
                  return weightItems.map((eq) => {
                    const ov = draft.overrides?.[eq.id] || {};
                      return (
                        <div key={eq.id} className="admin-tier-override-edit-row">
                          <label className="admin-tier-override-edit-label">{eq.name || eq.id}</label>
                          {(eq.has_weights || (Array.isArray(eq.weights_lb) && eq.weights_lb.length > 0)) && (
                          <input
                            type="text"
                            className="input input--sm"
                            placeholder={`Default: ${(eq.weights_lb || []).join(', ')}`}
                            value={ov.weights_lb ? ov.weights_lb.join(', ') : ''}
                            onChange={(e) => {
                              const vals = e.target.value ? e.target.value.split(',').map((n) => Number(n.trim())).filter((n) => !Number.isNaN(n)) : [];
                              updateOverride(eq.id, 'weights_lb', vals.length > 0 ? vals : null);
                            }}
                          />
                        )}
                        {(Array.isArray(eq.resistance_levels) && eq.resistance_levels.length > 0) && (
                          <input
                            type="text"
                            className="input input--sm"
                            placeholder={`Default: ${eq.resistance_levels.join(', ')}`}
                            value={ov.resistance_levels ? ov.resistance_levels.join(', ') : ''}
                            onChange={(e) => {
                              const vals = e.target.value ? e.target.value.split(',').map((s) => s.trim()).filter(Boolean) : [];
                              updateOverride(eq.id, 'resistance_levels', vals.length > 0 ? vals : null);
                            }}
                          />
                        )}
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          }
          footer={
            <>
              {saveError && <span style={{ color: 'var(--color-error)', fontSize: 'var(--font-size-sm)', marginRight: 'auto' }}>{saveError}</span>}
              <button type="button" className="btn btn--secondary btn--sm" onClick={closeModal} disabled={saving}>Cancel</button>
              <button type="button" className="btn btn--primary btn--sm" onClick={saveEdit} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
            </>
          }
        />
      )}
    </div>
  );
}

// ---- Equipment ----
function AdminEquipmentCatalog({ list, onAfterSave }) {
  const [modalItem, setModalItem] = useState(null);
  const [isAddMode, setIsAddMode] = useState(false);
  const [draft, setDraft] = useState({});
  const [localPhotoUrls, setLocalPhotoUrls] = useState({});
  const [uploadingId, setUploadingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const fileInputRef = useRef(null);
  const pendingUploadIdRef = useRef(null);

  const openEdit = (item) => {
    setModalItem(item);
    setIsAddMode(false);
    setSaveError(null);
    setDeleteError(null);
    setDraft({
      ...item,
      tags: Array.isArray(item.tags) ? item.tags.join(', ') : '',
      resistance_levels: Array.isArray(item.resistance_levels) ? item.resistance_levels : [],
      photo_url: item.photo_url || localPhotoUrls[item.id] || '',
      product_url: item.product_url || '',
      product_retailer: item.product_retailer || '',
    });
  };
  const openAddNew = () => {
    setModalItem(null);
    setIsAddMode(true);
    setSaveError(null);
    setDeleteError(null);
    setDraft({ ...EMPTY_EQUIPMENT });
  };
  const closeModal = () => { setModalItem(null); setIsAddMode(false); setDraft({}); setSaveError(null); setDeleteError(null); setShowDeleteConfirm(false); };

  const handleDeleteClick = () => setShowDeleteConfirm(true);
  const handleDeleteCancel = () => setShowDeleteConfirm(false);

  const handleDeleteConfirm = async () => {
    const id = modalItem?.id;
    if (!id) return;
    setDeleting(true);
    setDeleteError(null);
    setSaveError(null);
    try {
      await cascadeRemoveEquipmentFromTiers(id);
      await deleteEquipment(id);
      if (typeof onAfterSave === 'function') await onAfterSave();
      closeModal();
    } catch (err) {
      console.error('[AdminContent] Equipment delete failed:', err);
      setDeleteError(err.message || 'Delete failed');
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const isResistanceBand = (draft?.category || '') === 'resistance_band';
  const toggleResistanceLevel = (level) => {
    setDraft((d) => {
      const current = Array.isArray(d.resistance_levels) ? d.resistance_levels : [];
      const next = current.includes(level) ? current.filter((l) => l !== level) : [...current, level].sort();
      return { ...d, resistance_levels: next };
    });
  };

  const saveEdit = async () => {
    const id = (isAddMode ? draft.id : (modalItem && modalItem.id)) || '';
    const docId = toSlug(id) || id.trim();
    if (!docId) {
      setSaveError('ID is required (e.g. my_equipment)');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const tagsArr = typeof draft.tags === 'string'
        ? draft.tags.split(',').map((s) => s.trim()).filter(Boolean)
        : (Array.isArray(draft.tags) ? draft.tags : []);
      const isResBand = (draft.category || '') === 'resistance_band';
      const payload = {
        id: docId,
        name: draft.name || '',
        category: draft.category || '',
        manufacturer: draft.manufacturer || '',
        description: draft.description || '',
        tags: tagsArr,
        has_weights: isResBand ? false : (Array.isArray(draft.weights_lb) && draft.weights_lb.length > 0),
        weights_lb: isResBand ? [] : (Array.isArray(draft.weights_lb) ? draft.weights_lb : []),
        resistance_levels: isResBand ? (Array.isArray(draft.resistance_levels) ? draft.resistance_levels : []) : [],
        photo_url: draft.photo_url || null,
        product_url: draft.product_url || '',
        product_retailer: draft.product_retailer || '',
      };
      await updateEquipment(docId, payload);
      if (!payload.photo_url) {
        setLocalPhotoUrls((prev) => { const next = { ...prev }; delete next[docId]; return next; });
      }
      if (typeof onAfterSave === 'function') await onAfterSave();
      closeModal();
    } catch (err) {
      console.error('[AdminContent] Equipment save failed:', err);
      setSaveError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const effectiveId = isAddMode ? (draft.id || '').trim() || toSlug(draft.name) : (modalItem && modalItem.id);

  const handleEquipmentPhotoChange = async (e) => {
    const file = e.target.files?.[0];
    const id = pendingUploadIdRef.current;
    e.target.value = '';
    pendingUploadIdRef.current = null;
    if (!file || !id) return;
    if (!auth.currentUser) {
      console.error('[AdminContent] Equipment photo upload: not signed in');
      return;
    }
    setUploadingId(id);
    try {
      const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
      const storageRef = ref(storage, `content/equipment/${id}.${ext}`);
      const contentType = file.type || (ext === 'webp' ? 'image/webp' : 'image/jpeg');
      await uploadBytes(storageRef, file, { contentType });
      const url = await getDownloadURL(storageRef);
      setLocalPhotoUrls((prev) => ({ ...prev, [id]: url }));
      if (modalItem?.id === id) setDraft((d) => ({ ...d, photo_url: url }));
      try {
        await updateEquipment(id, { photo_url: url });
        if (typeof onAfterSave === 'function') await onAfterSave();
      } catch (firestoreErr) {
        console.error('[AdminContent] Firestore update (photo_url) failed:', firestoreErr);
        if (/permission|insufficient/i.test(firestoreErr?.message || '')) {
          console.warn('[AdminContent] Deploy Firestore rules: firebase deploy --only firestore');
        }
      }
    } catch (err) {
      console.error('[AdminContent] Equipment photo upload failed:', err);
      if (/permission|insufficient/i.test(err?.message || '')) {
        console.warn('[AdminContent] Deploy Storage rules: firebase deploy --only storage');
      }
    } finally {
      setUploadingId(null);
    }
  };

  const photoUrlFor = (item) => localPhotoUrls[item?.id] ?? item?.photo_url ?? (modalItem?.id === item?.id ? draft.photo_url : null);

  return (
    <div id="admin-equipment-catalog" className="admin-catalog admin-catalog--equipment">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,.webp,image/webp"
        aria-hidden="true"
        tabIndex={-1}
        style={{ position: 'absolute', width: 1, height: 1, opacity: 0, overflow: 'hidden' }}
        onChange={handleEquipmentPhotoChange}
      />
      <div className="admin-content-top">
        <div className="admin-content-top-heading">
          <h2 className="admin-content-section-title">Equipment</h2>
          <p className="admin-content-section-desc">Manage gym equipment — names, categories, photos, and weights. Edit or add items.</p>
        </div>
        <div className="admin-content-top-actions">
          <button type="button" className="admin-content-add-btn" onClick={openAddNew}>
            <span className="admin-content-add-icon">+</span>
            Add new equipment
          </button>
        </div>
      </div>
      <div className="admin-catalog-list">
        {list.map((item) => {
          const photoUrl = photoUrlFor(item);
          const isUploading = uploadingId === item.id;
          return (
            <div key={item.id} className="admin-catalog-card admin-catalog-card--equipment">
              <div className="admin-catalog-card-media">
                {photoUrl ? (
                  <img src={photoUrl} alt={item.name} className="admin-catalog-photo-img" />
                ) : (
                  <div
                    className="admin-catalog-photo-placeholder"
                    title="Upload equipment photo"
                    role="button"
                    tabIndex={0}
                    onClick={() => { pendingUploadIdRef.current = item.id; fileInputRef.current?.click(); }}
                    onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); pendingUploadIdRef.current = item.id; fileInputRef.current?.click(); } }}
                    style={{ cursor: isUploading ? 'wait' : 'pointer' }}
                  >
                    <span className="text-muted text-sm">{isUploading ? 'Uploading…' : 'Photo'}</span>
                    <span className="admin-catalog-upload-hint">{isUploading ? '' : 'Upload'}</span>
                  </div>
                )}
              </div>
              <div className="admin-catalog-card-body">
                <div className="admin-catalog-card-content">
                  <div className="admin-catalog-card-header">
                    <strong className="admin-catalog-name">{item.name}</strong>
                    <span className="admin-catalog-id text-muted text-sm">{item.id}</span>
                  </div>
                <dl className="admin-catalog-meta">
                  {item.category && <><dt>Category</dt><dd>{item.category.replace(/_/g, ' ')}</dd></>}
                  {item.description && <><dt>Description</dt><dd>{item.description}</dd></>}
                  {item.manufacturer && <><dt>Manufacturer</dt><dd>{item.manufacturer}</dd></>}
                  {Array.isArray(item.tags) && item.tags.length > 0 && <><dt>Tags</dt><dd>{item.tags.join(', ')}</dd></>}
                  {Array.isArray(item.weights_lb) && item.weights_lb.length > 0 && <><dt>Weights (lb)</dt><dd>{item.weights_lb.join(', ')}</dd></>}
                  {Array.isArray(item.resistance_levels) && item.resistance_levels.length > 0 && <><dt>Resistance</dt><dd>{item.resistance_levels.map((l) => l.charAt(0).toUpperCase() + l.slice(1)).join(', ')}</dd></>}
                  {item.product_url && <><dt>Product</dt><dd><a href={item.product_url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-primary)' }}>{item.product_retailer || 'Link'}</a></dd></>}
                </dl>
                </div>
                <div className="admin-catalog-edit-row">
                  <button type="button" className="admin-catalog-edit-btn" onClick={() => openEdit(item)} aria-label="Edit">
                    <img src={EditIcon} alt="" className="admin-catalog-edit-icon" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {(modalItem || isAddMode) && (
        <ContentEditModal
          title={isAddMode ? 'Add new equipment' : `Edit: ${modalItem.name}`}
          onClose={closeModal}
          mediaSection={
            <div className="admin-content-edit-modal-media">
              {!isAddMode && (effectiveId && (photoUrlFor(modalItem) ? (
                <img src={photoUrlFor(modalItem)} alt={modalItem.name} className="admin-catalog-photo-img" />
              ) : (
                <div
                  className="admin-catalog-photo-placeholder"
                  title="Upload equipment photo"
                  role="button"
                  tabIndex={0}
                  onClick={() => { pendingUploadIdRef.current = effectiveId; fileInputRef.current?.click(); }}
                  onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); pendingUploadIdRef.current = effectiveId; fileInputRef.current?.click(); } }}
                  style={{ cursor: uploadingId === effectiveId ? 'wait' : 'pointer' }}
                >
                  <span className="text-muted text-sm">{uploadingId === effectiveId ? 'Uploading…' : 'Photo'}</span>
                  <span className="admin-catalog-upload-hint">{uploadingId === effectiveId ? '' : 'Upload'}</span>
                </div>
              )))}
              {isAddMode && <div className="admin-catalog-photo-placeholder"><span className="text-muted text-sm">Set ID and save, then edit to add photo.</span></div>}
            </div>
          }
          body={
            <>
              <div className="admin-catalog-edit-fields">
                <div className="admin-catalog-field">
                  <label>ID</label>
                  {isAddMode ? (
                    <input type="text" className="input" value={draft.id || ''} onChange={(e) => setDraft((d) => ({ ...d, id: e.target.value }))} placeholder="e.g. my_equipment (lowercase, underscores)" />
                  ) : (
                    <input type="text" className="input" value={draft.id || ''} readOnly />
                  )}
                </div>
                <div className="admin-catalog-field">
                  <label>Name</label>
                  <input type="text" className="input" value={draft.name || ''} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} />
                </div>
                <div className="admin-catalog-field">
                  <label>Category</label>
                  <select className="input" value={draft.category || ''} onChange={(e) => setDraft((d) => ({ ...d, category: e.target.value }))}>
                    {EQUIPMENT_CATEGORIES.map((opt) => (
                      <option key={opt.value || '_'} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div className="admin-catalog-field">
                  <label>Manufacturer</label>
                  <input type="text" className="input" value={draft.manufacturer || ''} onChange={(e) => setDraft((d) => ({ ...d, manufacturer: e.target.value }))} />
                </div>
                <div className="admin-catalog-field admin-catalog-field--wide">
                  <label>Description</label>
                  <textarea className="input" rows={2} value={draft.description || ''} onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))} />
                </div>
                <div className="admin-catalog-field">
                  <label>Tags (comma-sep)</label>
                  <input type="text" className="input" value={draft.tags || ''} onChange={(e) => setDraft((d) => ({ ...d, tags: e.target.value }))} />
                </div>
                {isResistanceBand ? (
                  <div className="admin-catalog-field">
                    <label>Resistance level</label>
                    <div className="admin-catalog-resistance-levels">
                      {RESISTANCE_LEVELS.map(({ value, label }) => (
                        <label key={value} className="admin-catalog-resistance-option">
                          <input type="checkbox" checked={(draft.resistance_levels || []).includes(value)} onChange={() => toggleResistanceLevel(value)} />
                          <span>{label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="admin-catalog-field">
                    <label>Weights (lb, comma-sep)</label>
                    <input type="text" className="input" value={Array.isArray(draft.weights_lb) ? draft.weights_lb.join(', ') : ''} onChange={(e) => setDraft((d) => ({ ...d, weights_lb: e.target.value ? e.target.value.split(',').map((n) => Number(n.trim())).filter((n) => !Number.isNaN(n)) : [] }))} />
                  </div>
                )}
                <div className="admin-catalog-field admin-catalog-field--wide">
                  <label>Photo URL</label>
                  <input type="url" className="input" value={draft.photo_url || ''} onChange={(e) => setDraft((d) => ({ ...d, photo_url: e.target.value }))} placeholder="Upload above or paste URL" />
                </div>
                <div className="admin-catalog-field admin-catalog-field--wide">
                  <label>Product URL</label>
                  <input type="url" className="input" value={draft.product_url || ''} onChange={(e) => setDraft((d) => ({ ...d, product_url: e.target.value }))} placeholder="Link to product page (e.g. nike.com, roguefit...)" />
                </div>
                <div className="admin-catalog-field">
                  <label>Retailer</label>
                  <input type="text" className="input" value={draft.product_retailer || ''} onChange={(e) => setDraft((d) => ({ ...d, product_retailer: e.target.value }))} placeholder="e.g. Nike, Rogue, Amazon" />
                </div>
              </div>
            </>
          }
          footer={
            <>
              {!isAddMode && (
                <button type="button" className="admin-content-delete-link" onClick={handleDeleteClick} disabled={saving || deleting}>
                  {deleting ? 'Deleting…' : 'Delete'}
                </button>
              )}
              {(saveError || deleteError) && <span style={{ color: 'var(--color-error)', fontSize: 'var(--font-size-sm)', marginRight: 'auto' }}>{saveError || deleteError}</span>}
              <button type="button" className="btn btn--secondary btn--sm" onClick={closeModal} disabled={saving || deleting}>Cancel</button>
              <button type="button" className="btn btn--primary btn--sm" onClick={saveEdit} disabled={saving || deleting}>{saving ? 'Saving…' : 'Save'}</button>
            </>
          }
        />
      )}

      {showDeleteConfirm && (
        <div className="admin-content-edit-modal-overlay admin-content-confirm-overlay" onClick={(e) => e.target === e.currentTarget && handleDeleteCancel()}>
          <div className="admin-content-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Are you sure?</h3>
            <p>Delete &quot;{modalItem?.name}&quot;? This will remove it from all equipment tiers and delete it from the database. This cannot be undone.</p>
            <div className="admin-content-confirm-buttons">
              <button type="button" className="btn btn--secondary" onClick={handleDeleteCancel} disabled={deleting}>No, cancel</button>
              <button type="button" className="btn btn--primary" onClick={handleDeleteConfirm} disabled={deleting}>{deleting ? 'Deleting…' : 'Yes, delete'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function getExerciseGroups(list) {
  const groups = {};
  list.forEach((item) => {
    const key = (item?.muscles_primary?.[0] ?? '_other').toLowerCase();
    if (!groups[key]) groups[key] = [];
    groups[key].push(item);
  });
  const sortedKeys = Object.keys(groups).sort((a, b) => a.localeCompare(b));
  const label = (k) => (k === '_other' ? 'Other' : k.charAt(0).toUpperCase() + k.slice(1).replace(/_/g, ' '));
  return { groups, sortedKeys, label };
}

// ---- Exercises ----
function AdminExercisesCatalog({ list, onAfterSave }) {
  const [modalItem, setModalItem] = useState(null);
  const [isAddMode, setIsAddMode] = useState(false);
  const [draft, setDraft] = useState({});
  const [localVideoUrls, setLocalVideoUrls] = useState({});
  const [uploadingId, setUploadingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [playingYouTubeId, setPlayingYouTubeId] = useState(null);
  const [tagFilter, setTagFilter] = useState('');
  const [taggingId, setTaggingId] = useState(null);
  const videoInputRef = useRef(null);
  const pendingVideoIdRef = useRef(null);

  const filteredList = useMemo(() => {
    if (!tagFilter) return list;
    const tag = tagFilter.toLowerCase();
    return (list || []).filter((e) => {
      const tags = Array.isArray(e.tags)
        ? e.tags
        : (typeof e.tags === 'string' ? e.tags.split(',').map((s) => s.trim()).filter(Boolean) : []);
      return tags.map((t) => String(t).toLowerCase()).includes(tag);
    });
  }, [list, tagFilter]);

  const { groups, sortedKeys, label } = useMemo(() => getExerciseGroups(filteredList), [filteredList]);

  const openEdit = (item) => {
    const media = item.media || {};
    const tags = Array.isArray(item.tags)
      ? item.tags
      : (typeof item.tags === 'string' ? item.tags.split(',').map((s) => s.trim()).filter(Boolean) : []);
    const roleValues = EXERCISE_ROLE_TAGS.map((t) => t.value);
    const tagsRole = tags.filter((t) => roleValues.includes(String(t).toLowerCase()));
    const otherTags = tags.filter((t) => !roleValues.includes(String(t).toLowerCase()));
    setModalItem(item);
    setIsAddMode(false);
    setSaveError(null);
    setDeleteError(null);
    setDraft({
      ...item,
      muscles_primary: Array.isArray(item.muscles_primary) ? item.muscles_primary.join(', ') : '',
      equipment: Array.isArray(item.equipment) ? item.equipment.join(', ') : '',
      tags: tagsRole,
      tagsOther: otherTags.join(', '),
      media: { ...media, video: media.video || localVideoUrls[item.id] || '' },
    });
  };
  const openAddNew = () => {
    setModalItem(null);
    setIsAddMode(true);
    setSaveError(null);
    setDeleteError(null);
    setDraft({ ...EMPTY_EXERCISE, tagsOther: '' });
  };
  const closeModal = () => { setModalItem(null); setIsAddMode(false); setDraft({}); setSaveError(null); setDeleteError(null); setPlayingYouTubeId(null); };

  const toggleExerciseTag = (tagValue) => {
    setDraft((d) => {
      const current = Array.isArray(d.tags) ? d.tags : [];
      const has = current.map((t) => String(t).toLowerCase()).includes(tagValue.toLowerCase());
      const next = has
        ? current.filter((t) => String(t).toLowerCase() !== tagValue.toLowerCase())
        : [...current, tagValue];
      return { ...d, tags: next };
    });
  };

  const setTagsOther = (val) => setDraft((d) => ({ ...d, tagsOther: val }));

  const quickToggleTag = async (item, tagValue) => {
    const id = item?.id;
    if (!id) return;
    setTaggingId(id);
    try {
      const tags = Array.isArray(item.tags)
        ? [...item.tags]
        : (typeof item.tags === 'string' ? item.tags.split(',').map((s) => s.trim()).filter(Boolean) : []);
      const has = tags.map((t) => String(t).toLowerCase()).includes(tagValue.toLowerCase());
      const nextTags = has
        ? tags.filter((t) => String(t).toLowerCase() !== tagValue.toLowerCase())
        : [...tags, tagValue];
      await updateExercise(id, { tags: nextTags });
      if (typeof onAfterSave === 'function') await onAfterSave();
    } catch (err) {
      console.error('[AdminContent] Quick tag toggle failed:', err);
    } finally {
      setTaggingId(null);
    }
  };

  const saveEdit = async () => {
    const id = (isAddMode ? draft.id : (modalItem && modalItem.id)) || '';
    const docId = toSlug(id) || id.trim();
    if (!docId) {
      setSaveError('ID is required (e.g. my_exercise)');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const musclesArr = typeof draft.muscles_primary === 'string'
        ? draft.muscles_primary.split(',').map((s) => s.trim()).filter(Boolean)
        : (Array.isArray(draft.muscles_primary) ? draft.muscles_primary : []);
      const equipmentArr = typeof draft.equipment === 'string'
        ? draft.equipment.split(',').map((s) => s.trim()).filter(Boolean)
        : (Array.isArray(draft.equipment) ? draft.equipment : []);
      const otherTags = typeof draft.tagsOther === 'string'
        ? draft.tagsOther.split(',').map((s) => s.trim()).filter(Boolean)
        : [];
      const tagsArr = [
        ...(Array.isArray(draft.tags) ? draft.tags : []),
        ...otherTags,
      ].filter((t, i, a) => a.findIndex((x) => String(x).toLowerCase() === String(t).toLowerCase()) === i);
      const payload = {
        id: docId,
        name: draft.name || '',
        difficulty: draft.difficulty || '',
        category: draft.category || '',
        movement_pattern: draft.movement_pattern || '',
        muscles_primary: musclesArr,
        equipment: equipmentArr,
        description: draft.description || '',
        tags: tagsArr,
        media: { ...(draft.media || {}), video: draft.media?.video || '' },
      };
      await updateExercise(docId, payload);
      if (typeof onAfterSave === 'function') await onAfterSave();
      closeModal();
    } catch (err) {
      console.error('[AdminContent] Exercise save failed:', err);
      setSaveError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const id = modalItem?.id;
    if (!id) return;
    if (!auth.currentUser) {
      setDeleteError('You must be signed in to delete.');
      return;
    }
    if (!window.confirm(`Delete exercise "${modalItem?.name || id}"? It will be removed from program palettes, warmups, and finishers. This cannot be undone.`)) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteExercise(id);
      try {
        await cascadeRemoveExerciseFromReferences(id);
      } catch {
        /* Exercise was deleted. Cascade (programs/warmups/finishers) failed — deploy firestore rules to enable it. No console spam. */
      }
      if (typeof onAfterSave === 'function') await onAfterSave();
      closeModal();
    } catch (err) {
      const isPermissionDenied = err.code === 'permission-denied' || (err.message && err.message.toLowerCase().includes('permission'));
      if (!isPermissionDenied) console.error('[AdminContent] Exercise delete failed:', err);
      setDeleteError(
        isPermissionDenied
          ? 'Permission denied. Deploy Firestore rules: firebase deploy --only firestore:rules'
          : (err.message || 'Delete failed')
      );
    } finally {
      setDeleting(false);
    }
  };

  const handleExerciseVideoChange = async (e) => {
    const file = e.target.files?.[0];
    const id = pendingVideoIdRef.current;
    e.target.value = '';
    pendingVideoIdRef.current = null;
    if (!file || !id) return;
    setUploadingId(id);
    try {
      const ext = file.name.split('.').pop() || 'mp4';
      const storageRef = ref(storage, `content/exercises/${id}/video.${ext}`);
      const contentType = file.type || (ext === 'webp' ? 'image/webp' : 'video/mp4');
      await uploadBytes(storageRef, file, { contentType });
      const url = await getDownloadURL(storageRef);
      setLocalVideoUrls((prev) => ({ ...prev, [id]: url }));
      if (modalItem?.id === id) setDraft((d) => ({ ...d, media: { ...(d.media || {}), video: url } }));
      // Persist media.video to Firestore immediately so it survives refresh
      await updateExerciseVideoUrl(id, url);
      if (typeof onAfterSave === 'function') await onAfterSave();
    } catch (err) {
      console.error('[AdminContent] Exercise video upload failed:', err);
    } finally {
      setUploadingId(null);
    }
  };

  const videoUrlFor = (item) => localVideoUrls[item?.id] ?? item?.media?.video ?? (modalItem?.id === item?.id ? draft.media?.video : null);
  const effectiveId = isAddMode ? (draft.id || '').trim() || toSlug(draft.name) : (modalItem && modalItem.id);

  return (
    <div className="admin-catalog admin-catalog--exercises">
      <input
        ref={videoInputRef}
        type="file"
        accept="video/*"
        aria-hidden="true"
        tabIndex={-1}
        style={{ position: 'absolute', width: 1, height: 1, opacity: 0, overflow: 'hidden' }}
        onChange={handleExerciseVideoChange}
      />
      <div className="admin-catalog-scroll-viewport admin-catalog-scroll-viewport--exercises">
        <div className="admin-content-top">
          <div className="admin-content-top-heading">
            <h2 className="admin-content-section-title">Exercises</h2>
            <p className="admin-content-section-desc">Manage exercises — names, difficulty, categories, movement patterns, and videos. Edit or add items.</p>
          </div>
        </div>
        <div className="admin-catalog-toolbar">
          <div className="admin-catalog-filter">
            <label htmlFor="exercise-tag-filter" className="admin-catalog-filter-label">Filter by tag</label>
            <select
              id="exercise-tag-filter"
              className="admin-catalog-filter-select"
              value={tagFilter}
              onChange={(e) => setTagFilter(e.target.value)}
            >
              <option value="">All exercises</option>
              {EXERCISE_ROLE_TAGS.map(({ value, label }) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
          <button type="button" className="admin-content-add-btn" onClick={openAddNew}>
            <span className="admin-content-add-icon">+</span>
            Add new exercise
          </button>
        </div>
        {sortedKeys.length > 0 && (
          <nav className="admin-catalog-nav" aria-label="Jump to a muscle group — scrolls with the page so you can move between sections faster">
            {sortedKeys.map((key) => (
              <a
                key={key}
                href={`#exercise-section-${key}`}
                className="admin-catalog-nav-link"
                onClick={(e) => {
                  const el = document.getElementById(`exercise-section-${key}`);
                  if (el) {
                    e.preventDefault();
                    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }}
              >
                {label(key)}
              </a>
            ))}
          </nav>
        )}
        <div className="admin-catalog-list admin-catalog-list--by-muscle">
        {sortedKeys.map((key) => (
          <section key={key} id={`exercise-section-${key}`} className="admin-catalog-group" aria-labelledby={`exercise-section-title-${key}`}>
            <h3 id={`exercise-section-title-${key}`} className="admin-catalog-group-title">{label(key)}</h3>
            <div className="admin-catalog-group-cards">
              {groups[key].map((item) => {
                const videoUrl = videoUrlFor(item);
                const isUploading = uploadingId === item.id;
                return (
            <div key={item.id} className="admin-catalog-card admin-catalog-card--exercise">
              <div className="admin-catalog-card-media admin-catalog-video-placeholder-wrap">
                {videoUrl ? (
                  <div className="admin-catalog-video-wrap" onClick={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()}>
                    {isYouTubeUrl(videoUrl) && getYouTubeEmbedUrl(videoUrl) ? (
                      playingYouTubeId === item.id ? (
                        <>
                          <iframe
                            src={getYouTubeEmbedUrl(videoUrl)}
                            title={item.name ? `${item.name} video` : 'Exercise video'}
                            className="admin-catalog-video-el admin-catalog-video-iframe"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                          <button type="button" className="admin-catalog-youtube-close" onClick={() => setPlayingYouTubeId(null)} aria-label="Close video">×</button>
                        </>
                      ) : (
                        <button
                          type="button"
                          className="admin-catalog-youtube-thumb"
                          onClick={() => setPlayingYouTubeId(item.id)}
                          aria-label="Play video"
                        >
                          <img src={`https://img.youtube.com/vi/${getYouTubeVideoId(videoUrl)}/hqdefault.jpg`} alt="" />
                          <span className="admin-catalog-youtube-play" aria-hidden>▶</span>
                        </button>
                      )
                    ) : (
                      <video src={videoUrl} className="admin-catalog-video-el" controls playsInline />
                    )}
                  </div>
                ) : (
                  <div
                    className="admin-catalog-video-placeholder"
                    title="Video URL or upload"
                    role="button"
                    tabIndex={0}
                    onClick={() => { pendingVideoIdRef.current = item.id; videoInputRef.current?.click(); }}
                    onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); pendingVideoIdRef.current = item.id; videoInputRef.current?.click(); } }}
                    style={{ cursor: isUploading ? 'wait' : 'pointer' }}
                  >
                    <span className="text-muted text-sm">{isUploading ? 'Uploading…' : 'Video'}</span>
                    <span className="admin-catalog-upload-hint">{isUploading ? '' : 'URL or upload'}</span>
                  </div>
                )}
              </div>
              <div className="admin-catalog-card-body">
                <div className="admin-catalog-card-content">
                  <div className="admin-catalog-card-header">
                    <strong className="admin-catalog-name">{item.name}</strong>
                    <span className="admin-catalog-id text-muted text-sm">{item.id}</span>
                  </div>
                  <dl className="admin-catalog-meta">
                    {item.difficulty && <><dt>Difficulty</dt><dd>{item.difficulty}</dd></>}
                    {item.category && <><dt>Category</dt><dd>{item.category}</dd></>}
                    {item.movement_pattern && <><dt>Movement</dt><dd>{item.movement_pattern}</dd></>}
                    {Array.isArray(item.muscles_primary) && item.muscles_primary.length > 0 && <><dt>Muscles</dt><dd>{item.muscles_primary.join(', ')}</dd></>}
                    {Array.isArray(item.equipment) && item.equipment.length > 0 && <><dt>Equipment</dt><dd>{item.equipment.join(', ')}</dd></>}
                    {(() => {
                      const tags = Array.isArray(item.tags) ? item.tags : [];
                      const other = tags.filter((t) => !EXERCISE_ROLE_TAGS.some((r) => r.value === String(t).toLowerCase()));
                      return other.length > 0 && <><dt>Tags</dt><dd>{other.join(', ')}</dd></>;
                    })()}
                    {item.description && <><dt>Description</dt><dd className="admin-catalog-desc">{item.description}</dd></>}
                  </dl>
                </div>
                <div className="admin-catalog-edit-row">
                  <button type="button" className="admin-catalog-edit-btn" onClick={() => openEdit(item)} aria-label="Edit">
                    <img src={EditIcon} alt="" className="admin-catalog-edit-icon" />
                  </button>
                  <div className="admin-exercise-tag-radials">
                    {EXERCISE_ROLE_TAGS.map(({ value, label }) => {
                      const tags = Array.isArray(item.tags)
                        ? item.tags
                        : (typeof item.tags === 'string' ? item.tags.split(',').map((s) => s.trim()).filter(Boolean) : []);
                      const isActive = tags.map((t) => String(t).toLowerCase()).includes(value);
                      const isTagging = taggingId === item.id;
                      return (
                        <button
                          key={value}
                          type="button"
                          className={`admin-exercise-tag-radial ${isActive ? 'admin-exercise-tag-radial--active' : ''}`}
                          onClick={(e) => { e.stopPropagation(); quickToggleTag(item, value); }}
                          disabled={isTagging}
                          title={`${isActive ? 'Remove' : 'Add'} ${label} tag`}
                          aria-pressed={isActive}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
                );
              })}
            </div>
          </section>
        ))}
        </div>
      </div>

      {(modalItem || isAddMode) && (
        <ContentEditModal
          title={isAddMode ? 'Add new exercise' : `Edit: ${modalItem.name}`}
          onClose={closeModal}
          mediaSection={
            <div className="admin-content-edit-modal-media">
              {!isAddMode && effectiveId && (videoUrlFor(modalItem) ? (
                <div className="admin-catalog-video-wrap" onClick={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()}>
                  {(() => {
                    const modalVideoUrl = videoUrlFor(modalItem);
                    if (isYouTubeUrl(modalVideoUrl) && getYouTubeEmbedUrl(modalVideoUrl)) {
                      return (
                        <iframe
                          src={getYouTubeEmbedUrl(modalVideoUrl)}
                          title={modalItem?.name ? `${modalItem.name} video` : 'Exercise video'}
                          className="admin-catalog-video-el admin-catalog-video-iframe"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      );
                    }
                    return <video src={modalVideoUrl} className="admin-catalog-video-el" controls playsInline />;
                  })()}
                </div>
              ) : (
                <div
                  className="admin-catalog-video-placeholder"
                  title="Video URL or upload"
                  role="button"
                  tabIndex={0}
                  onClick={() => { pendingVideoIdRef.current = effectiveId; videoInputRef.current?.click(); }}
                  onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); pendingVideoIdRef.current = effectiveId; videoInputRef.current?.click(); } }}
                  style={{ cursor: uploadingId === effectiveId ? 'wait' : 'pointer' }}
                >
                  <span className="text-muted text-sm">{uploadingId === effectiveId ? 'Uploading…' : 'Video'}</span>
                  <span className="admin-catalog-upload-hint">{uploadingId === effectiveId ? '' : 'URL or upload'}</span>
                </div>
              ))}
              {isAddMode && <div className="admin-catalog-video-placeholder"><span className="text-muted text-sm">Set ID and save, then edit to add video.</span></div>}
            </div>
          }
          body={
            <>
              <div className="admin-catalog-edit-fields">
                <div className="admin-catalog-field">
                  <label>ID</label>
                  {isAddMode ? (
                    <input type="text" className="input" value={draft.id || ''} onChange={(e) => setDraft((d) => ({ ...d, id: e.target.value }))} placeholder="e.g. my_exercise (lowercase, underscores)" />
                  ) : (
                    <input type="text" className="input" value={draft.id || ''} readOnly />
                  )}
                </div>
                <div className="admin-catalog-field">
                  <label>Name</label>
                  <input type="text" className="input" value={draft.name || ''} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} />
                </div>
                <div className="admin-catalog-field">
                  <label>Difficulty</label>
                  <input type="text" className="input" value={draft.difficulty || ''} onChange={(e) => setDraft((d) => ({ ...d, difficulty: e.target.value }))} />
                </div>
                <div className="admin-catalog-field">
                  <label>Category</label>
                  <input type="text" className="input" value={draft.category || ''} onChange={(e) => setDraft((d) => ({ ...d, category: e.target.value }))} />
                </div>
                <div className="admin-catalog-field">
                  <label>Movement pattern</label>
                  <input type="text" className="input" value={draft.movement_pattern || ''} onChange={(e) => setDraft((d) => ({ ...d, movement_pattern: e.target.value }))} />
                </div>
                <div className="admin-catalog-field">
                  <label>Muscles (comma-sep)</label>
                  <input type="text" className="input" value={Array.isArray(draft.muscles_primary) ? draft.muscles_primary.join(', ') : (draft.muscles_primary || '')} onChange={(e) => setDraft((d) => ({ ...d, muscles_primary: e.target.value }))} />
                </div>
                <div className="admin-catalog-field">
                  <label>Equipment (comma-sep)</label>
                  <input type="text" className="input" value={Array.isArray(draft.equipment) ? draft.equipment.join(', ') : (draft.equipment || '')} onChange={(e) => setDraft((d) => ({ ...d, equipment: e.target.value }))} />
                </div>
                <div className="admin-catalog-field">
                  <label>Tags</label>
                  <div className="admin-catalog-tags-row" style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)', marginBottom: 'var(--space-1)' }}>
                    {EXERCISE_ROLE_TAGS.map(({ value, label }) => (
                      <label key={value} className="admin-catalog-resistance-option" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)' }}>
                        <input
                          type="checkbox"
                          checked={(draft.tags || []).map((t) => String(t).toLowerCase()).includes(value)}
                          onChange={() => toggleExerciseTag(value)}
                        />
                        <span>{label}</span>
                      </label>
                    ))}
                  </div>
                  <input
                    type="text"
                    className="input"
                    value={draft.tagsOther ?? ''}
                    onChange={(e) => setTagsOther(e.target.value)}
                    placeholder="Other tags (comma-sep, e.g. lower_body, functional)"
                  />
                </div>
                <div className="admin-catalog-field admin-catalog-field--wide">
                  <label>Description</label>
                  <textarea className="input" rows={3} value={draft.description || ''} onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))} />
                </div>
                <div className="admin-catalog-field admin-catalog-field--wide">
                  <label>Video URL</label>
                  <input type="url" className="input" value={draft.media?.video || ''} onChange={(e) => setDraft((d) => ({ ...d, media: { ...(d.media || {}), video: e.target.value } }))} placeholder="Upload above or paste URL" />
                </div>
              </div>
            </>
          }
          footer={
            <>
              {!isAddMode && (
                <button type="button" className="btn btn--secondary btn--sm admin-content-delete-btn" onClick={handleDelete} disabled={saving || deleting} style={{ marginRight: 'auto' }}>
                  {deleting ? 'Deleting…' : 'Delete'}
                </button>
              )}
              {(saveError || deleteError) && <span style={{ color: 'var(--color-error)', fontSize: 'var(--font-size-sm)', marginRight: 'auto' }}>{saveError || deleteError}</span>}
              <button type="button" className="btn btn--secondary btn--sm" onClick={closeModal} disabled={saving || deleting}>Cancel</button>
              <button type="button" className="btn btn--primary btn--sm" onClick={saveEdit} disabled={saving || deleting}>{saving ? 'Saving…' : 'Save'}</button>
            </>
          }
        />
      )}
    </div>
  );
}

// Infer program from warmup id when program field is missing (backward compat)
function getWarmupProgram(item) {
  if (item?.program != null && item.program !== '') return item.program;
  const id = (item?.id || '').toLowerCase();
  if (id.startsWith('vb_') || id.includes('volleyball')) return 'volleyball';
  if (id.startsWith('multi_sport_') || id.includes('hiit_multi_sport')) return 'multi_sport_sprint';
  if (id.startsWith('hiit_volleyball')) return 'volleyball';
  return null; // shared
}

// ---- Warmups: name, duration, exercises table (exercise_id, sets, reps, rest_seconds, notes) ----
function AdminWarmupsCatalog({ list, exercisesList, exercisesListForLookup, programOptions = WARMUP_PROGRAM_OPTIONS, onAfterSave }) {
  const [modalItem, setModalItem] = useState(null);
  const [isAddMode, setIsAddMode] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [programFilter, setProgramFilter] = useState('');

  const filteredList = useMemo(() => {
    const raw = list || [];
    if (!programFilter) return raw;
    if (programFilter === 'shared') return raw.filter((item) => getWarmupProgram(item) == null);
    return raw.filter((item) => getWarmupProgram(item) === programFilter);
  }, [list, programFilter]);

  // Filter to warmup exercises only: tags includes "warmup" OR category is "mobility"
  const warmupExercisesOnly = useMemo(() => {
    const raw = exercisesList || [];
    return raw.filter((e) => {
      const tags = Array.isArray(e.tags)
        ? e.tags
        : (typeof e.tags === 'string' ? e.tags.split(',').map((s) => s.trim()).filter(Boolean) : []);
      const hasWarmupTag = tags.some((t) => String(t).toLowerCase() === 'warmup');
      const isMobility = (e.category || '').toLowerCase() === 'mobility';
      return hasWarmupTag || isMobility;
    }).sort((a, b) => (String(a?.name ?? '').toLowerCase()).localeCompare(String(b?.name ?? '').toLowerCase()));
  }, [exercisesList]);

  const openEdit = (item) => {
    setModalItem(item);
    setIsAddMode(false);
    setSaveError(null);
    setDeleteError(null);
    setDraft({
      ...item,
      program: item?.program ?? getWarmupProgram(item) ?? 'shared',
      exercises: Array.isArray(item.exercises) ? item.exercises.map((e) => ({ ...e })) : [],
    });
  };
  const openAddNew = () => {
    setModalItem(null);
    setIsAddMode(true);
    setSaveError(null);
    setDeleteError(null);
    setDraft({ ...EMPTY_WARMUP, program: null });
  };
  const closeModal = () => { setModalItem(null); setIsAddMode(false); setDraft(null); setSaveError(null); setDeleteError(null); };

  const handleDelete = async () => {
    const id = modalItem?.id;
    if (!id) return;
    if (!window.confirm(`Delete warmup "${modalItem?.name || id}"? This cannot be undone.`)) return;
    setDeleting(true);
    setDeleteError(null);
    setSaveError(null);
    try {
      await deleteWarmupTemplate(id);
      if (typeof onAfterSave === 'function') await onAfterSave();
      closeModal();
    } catch (err) {
      console.error('[AdminContent] Warmup delete failed:', err);
      setDeleteError(err.message || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const saveEdit = async () => {
    const id = (isAddMode ? draft?.id : (modalItem && modalItem.id)) || '';
    const docId = toSlug(id) || String(id).trim();
    if (!docId) {
      setSaveError('ID is required (e.g. my_warmup)');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const payload = {
        id: docId,
        name: draft.name || '',
        program: draft.program === 'shared' ? null : (draft.program || null),
        duration_minutes: draft.duration_minutes ?? 0,
        exercises: (draft.exercises || []).map((ex) => ({
          exercise_id: ex.exercise_id || '',
          sets: Number(ex.sets) || 0,
          reps: Number(ex.reps) || 0,
          rest_seconds: Number(ex.rest_seconds) || 0,
          notes: ex.notes || '',
        })),
      };
      await updateWarmupTemplate(docId, payload);
      if (typeof onAfterSave === 'function') await onAfterSave();
      closeModal();
    } catch (err) {
      console.error('[AdminContent] Warmup save failed:', err);
      setSaveError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const updateExerciseRow = (index, field, value) => {
    setDraft((d) => ({
      ...d,
      exercises: (d.exercises || []).map((ex, i) =>
        i === index ? { ...ex, [field]: field === 'sets' || field === 'reps' || field === 'rest_seconds' ? (value === '' ? 0 : Number(value)) : value } : ex
      ),
    }));
  };
  const addExerciseRow = () => {
    setDraft((d) => ({ ...d, exercises: [...(d.exercises || []), { exercise_id: '', sets: 1, reps: 0, rest_seconds: 0, notes: '' }] }));
  };
  const removeExerciseRow = (index) => {
    setDraft((d) => ({ ...d, exercises: (d.exercises || []).filter((_, i) => i !== index) }));
  };

  const exerciseName = (id) => {
    const lookupList = exercisesListForLookup || exercisesList || [];
    const ex = lookupList.find((e) => e.id === id);
    return ex ? ex.name : id || '—';
  };

  return (
    <div className="admin-catalog admin-catalog--warmups">
      <div className="admin-content-top">
        <div className="admin-content-top-heading">
          <h2 className="admin-content-section-title">Warmups</h2>
          <p className="admin-content-section-desc">Manage warmup templates — short exercise sequences used before workouts. Edit or add templates.</p>
        </div>
        <div className="admin-content-top-actions">
          <div className="admin-catalog-filter">
            <label htmlFor="warmup-program-filter" className="admin-catalog-filter-label">Program</label>
            <select
              id="warmup-program-filter"
              className="admin-catalog-filter-select"
              value={programFilter}
              onChange={(e) => setProgramFilter(e.target.value)}
            >
              {programOptions.map(({ value, label }) => (
                <option key={value || '_all'} value={value}>{label}</option>
              ))}
            </select>
          </div>
          <button type="button" className="admin-content-add-btn" onClick={openAddNew}>
            <span className="admin-content-add-icon">+</span>
            Add new warmup
          </button>
        </div>
      </div>
      <div className="admin-catalog-list admin-catalog-list--warmups">
        {filteredList.map((item) => (
          <div key={item.id} className="admin-catalog-card admin-catalog-card--warmup">
            <div className="admin-catalog-card-body admin-catalog-card-body--full">
              <div className="admin-catalog-card-content">
                <div className="admin-catalog-card-header">
                  <strong className="admin-catalog-name">{item.name}</strong>
                  <span className="admin-catalog-id text-muted text-sm">{item.id}</span>
                  <span className="admin-catalog-program-badge" title={`Program: ${getWarmupProgram(item) ?? 'shared'}`}>
                    {getWarmupProgram(item) ?? 'shared'}
                  </span>
                </div>
                <dl className="admin-catalog-meta">
                  {item.duration_minutes != null && <><dt>Duration (min)</dt><dd>{item.duration_minutes}</dd></>}
                </dl>
                {Array.isArray(item.exercises) && item.exercises.length > 0 && (
                <div className="admin-catalog-exercises-section">
                  <strong className="admin-catalog-exercises-title">Exercises & sets</strong>
                  <table className="admin-catalog-exercises-table">
                    <thead>
                      <tr>
                        <th>Exercise</th>
                        <th>Sets</th>
                        <th>Reps</th>
                        <th>Rest (s)</th>
                        <th>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {item.exercises.map((ex, i) => (
                        <tr key={i}>
                          <td>{exerciseName(ex.exercise_id)}</td>
                          <td>{ex.sets ?? '—'}</td>
                          <td>{ex.reps ?? '—'}</td>
                          <td>{ex.rest_seconds ?? '—'}</td>
                          <td>{ex.notes || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              </div>
              <div className="admin-catalog-edit-row">
                <button type="button" className="admin-catalog-edit-btn" onClick={() => openEdit(item)} aria-label="Edit">
                  <img src={EditIcon} alt="" className="admin-catalog-edit-icon" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {(modalItem || isAddMode) && draft && (
        <ContentEditModal
          title={isAddMode ? 'Add new warmup' : `Edit: ${modalItem.name}`}
          onClose={closeModal}
          body={
            <>
              <div className="admin-catalog-edit-fields">
                <div className="admin-catalog-field">
                  <label>ID</label>
                  {isAddMode ? (
                    <input type="text" className="input" value={draft.id || ''} onChange={(e) => setDraft((d) => ({ ...d, id: e.target.value }))} placeholder="e.g. my_warmup (lowercase, underscores)" />
                  ) : (
                    <input type="text" className="input" value={draft.id || ''} readOnly />
                  )}
                </div>
                <div className="admin-catalog-field">
                  <label>Name</label>
                  <input type="text" className="input" value={draft.name || ''} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} />
                </div>
                <div className="admin-catalog-field">
                  <label>Duration (min)</label>
                  <input type="number" className="input" min={0} value={draft.duration_minutes ?? ''} onChange={(e) => setDraft((d) => ({ ...d, duration_minutes: e.target.value === '' ? 0 : Number(e.target.value) }))} />
                </div>
                <div className="admin-catalog-field">
                  <label>Program</label>
                  <select
                    className="input"
                    value={draft.program ?? 'shared'}
                    onChange={(e) => setDraft((d) => ({ ...d, program: e.target.value }))}
                  >
                    {programOptions.filter((o) => o.value).map(({ value, label }) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="admin-catalog-exercises-section">
                <strong className="admin-catalog-exercises-title">Exercises & sets</strong>
                <table className="admin-catalog-exercises-table">
                  <thead>
                    <tr>
                      <th>Exercise</th>
                      <th>Sets</th>
                      <th>Reps</th>
                      <th>Rest (s)</th>
                      <th>Notes</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(draft.exercises || []).map((ex, i) => (
                      <tr key={i}>
                        <td>
                          <select
                            className="input input--sm"
                            value={ex.exercise_id || ''}
                            onChange={(e) => updateExerciseRow(i, 'exercise_id', e.target.value)}
                          >
                            <option value="">—</option>
                            {warmupExercisesOnly.map((exItem) => (
                              <option key={exItem.id} value={exItem.id}>{exItem.name || exItem.id}</option>
                            ))}
                          </select>
                        </td>
                        <td><input type="number" className="input input--sm" min={0} value={ex.sets ?? ''} onChange={(e) => updateExerciseRow(i, 'sets', e.target.value)} /></td>
                        <td><input type="number" className="input input--sm" min={0} value={ex.reps ?? ''} onChange={(e) => updateExerciseRow(i, 'reps', e.target.value)} /></td>
                        <td><input type="number" className="input input--sm" min={0} value={ex.rest_seconds ?? ''} onChange={(e) => updateExerciseRow(i, 'rest_seconds', e.target.value)} /></td>
                        <td><input type="text" className="input input--sm" value={ex.notes || ''} onChange={(e) => updateExerciseRow(i, 'notes', e.target.value)} placeholder="Notes" /></td>
                        <td><button type="button" className="btn btn--ghost btn--sm" onClick={() => removeExerciseRow(i)} aria-label="Remove">×</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <button type="button" className="btn btn--secondary btn--sm" onClick={addExerciseRow}>+ Add exercise</button>
              </div>
            </>
          }
          footer={
            <>
              {!isAddMode && (
                <button type="button" className="btn btn--secondary btn--sm admin-content-delete-btn" onClick={handleDelete} disabled={saving || deleting} style={{ marginRight: 'auto' }}>
                  {deleting ? 'Deleting…' : 'Delete'}
                </button>
              )}
              {(saveError || deleteError) && <span style={{ color: 'var(--color-error)', fontSize: 'var(--font-size-sm)', marginRight: 'auto' }}>{saveError || deleteError}</span>}
              <button type="button" className="btn btn--secondary btn--sm" onClick={closeModal} disabled={saving || deleting}>Cancel</button>
              <button type="button" className="btn btn--primary btn--sm" onClick={saveEdit} disabled={saving || deleting}>{saving ? 'Saving…' : 'Save'}</button>
            </>
          }
        />
      )}
    </div>
  );
}

// ---- Drills: name, duration, description, exercises table — for bonus/offseason power & speed ----
function AdminDrillsCatalog({ list, exercisesList, exercisesListForLookup, onAfterSave }) {
  const [modalItem, setModalItem] = useState(null);
  const [isAddMode, setIsAddMode] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const openEdit = (item) => {
    setModalItem(item);
    setIsAddMode(false);
    setSaveError(null);
    setDraft({
      ...item,
      warmup_exercises: Array.isArray(item.warmup_exercises) ? item.warmup_exercises.map((e) => ({ ...e })) : [],
      exercises: Array.isArray(item.exercises) ? item.exercises.map((e) => ({ ...e })) : [],
      finisher_exercises: Array.isArray(item.finisher_exercises) ? item.finisher_exercises.map((e) => ({ ...e })) : [],
    });
  };
  const openAddNew = () => {
    setModalItem(null);
    setIsAddMode(true);
    setSaveError(null);
    setDraft({ ...EMPTY_DRILL, warmup_exercises: [], exercises: [], finisher_exercises: [] });
  };
  const closeModal = () => { setModalItem(null); setIsAddMode(false); setDraft(null); setSaveError(null); };

  const mapExercises = (arr) => (arr || []).map((ex) => ({
    exercise_id: ex.exercise_id || '',
    sets: Number(ex.sets) || 0,
    reps: Number(ex.reps) || 0,
    rest_seconds: Number(ex.rest_seconds) || 0,
    notes: ex.notes || '',
  }));

  const saveEdit = async () => {
    const id = (isAddMode ? draft?.id : (modalItem && modalItem.id)) || '';
    const docId = toSlug(id) || String(id).trim();
    if (!docId) {
      setSaveError('ID is required (e.g. power_speed_drills)');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const payload = {
        id: docId,
        name: draft.name || '',
        duration_minutes: draft.duration_minutes ?? 0,
        description: draft.description || '',
        warmup_exercises: mapExercises(draft.warmup_exercises),
        exercises: mapExercises(draft.exercises),
        finisher_exercises: mapExercises(draft.finisher_exercises),
      };
      await updateDrillTemplate(docId, payload);
      if (typeof onAfterSave === 'function') await onAfterSave();
      closeModal();
    } catch (err) {
      console.error('[AdminContent] Drill save failed:', err);
      setSaveError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const updateExerciseRow = (section, index, field, value) => {
    setDraft((d) => ({
      ...d,
      [section]: (d[section] || []).map((ex, i) =>
        i === index ? { ...ex, [field]: field === 'sets' || field === 'reps' || field === 'rest_seconds' ? (value === '' ? 0 : Number(value)) : value } : ex
      ),
    }));
  };
  const addExerciseRow = (section) => {
    setDraft((d) => ({ ...d, [section]: [...(d[section] || []), { exercise_id: '', sets: 1, reps: 0, rest_seconds: 0, notes: '' }] }));
  };
  const removeExerciseRow = (section, index) => {
    setDraft((d) => ({ ...d, [section]: (d[section] || []).filter((_, i) => i !== index) }));
  };

  const exerciseName = (id) => {
    const lookupList = exercisesListForLookup || exercisesList || [];
    const ex = lookupList.find((e) => e.id === id);
    return ex ? ex.name : id || '—';
  };

  return (
    <div className="admin-catalog admin-catalog--drills">
      <div className="admin-content-top">
        <div className="admin-content-top-heading">
          <h2 className="admin-content-section-title">Drills</h2>
          <p className="admin-content-section-desc">Manage drill templates for bonus and offseason workouts — power and speed exercises. Tag exercises as &quot;Drills&quot; in the Exercises section to use them here.</p>
        </div>
        <div className="admin-content-top-actions">
          <button type="button" className="admin-content-add-btn" onClick={openAddNew}>
            <span className="admin-content-add-icon">+</span>
            Add new drill
          </button>
        </div>
      </div>
      <div className="admin-catalog-list admin-catalog-list--drills">
        {list.map((item) => (
          <div key={item.id} className="admin-catalog-card admin-catalog-card--drill">
            <div className="admin-catalog-card-body admin-catalog-card-body--full">
              <div className="admin-catalog-card-content">
                <div className="admin-catalog-card-header">
                  <strong className="admin-catalog-name">{item.name}</strong>
                  <span className="admin-catalog-id text-muted text-sm">{item.id}</span>
                </div>
                <dl className="admin-catalog-meta">
                  {item.duration_minutes != null && <><dt>Duration (min)</dt><dd>{item.duration_minutes}</dd></>}
                  {item.description && <><dt>Description</dt><dd className="admin-catalog-desc">{item.description}</dd></>}
                </dl>
                {[
                  { key: 'warmup_exercises', label: 'Warmup' },
                  { key: 'exercises', label: 'Drills' },
                  { key: 'finisher_exercises', label: 'Finisher' },
                ].map(({ key, label }) => {
                  const rows = Array.isArray(item[key]) ? item[key] : [];
                  if (rows.length === 0) return null;
                  return (
                    <div key={key} className="admin-catalog-exercises-section">
                      <strong className="admin-catalog-exercises-title">{label} ({rows.length})</strong>
                      <table className="admin-catalog-exercises-table">
                        <thead>
                          <tr><th>Exercise</th><th>Sets</th><th>Reps</th><th>Rest (s)</th><th>Notes</th></tr>
                        </thead>
                        <tbody>
                          {rows.map((ex, i) => (
                            <tr key={i}>
                              <td>{exerciseName(ex.exercise_id)}</td>
                              <td>{ex.sets ?? '—'}</td>
                              <td>{ex.reps ?? '—'}</td>
                              <td>{ex.rest_seconds ?? '—'}</td>
                              <td>{ex.notes || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  );
                })}
              </div>
              <div className="admin-catalog-edit-row">
                <button type="button" className="admin-catalog-edit-btn" onClick={() => openEdit(item)} aria-label="Edit">
                  <img src={EditIcon} alt="" className="admin-catalog-edit-icon" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {(modalItem || isAddMode) && draft && (
        <ContentEditModal
          title={isAddMode ? 'Add new drill' : `Edit: ${modalItem.name}`}
          onClose={closeModal}
          body={
            <>
              <div className="admin-catalog-edit-fields">
                <div className="admin-catalog-field">
                  <label>ID</label>
                  {isAddMode ? (
                    <input type="text" className="input" value={draft.id || ''} onChange={(e) => setDraft((d) => ({ ...d, id: e.target.value }))} placeholder="e.g. power_speed_drills (lowercase, underscores)" />
                  ) : (
                    <input type="text" className="input" value={draft.id || ''} readOnly />
                  )}
                </div>
                <div className="admin-catalog-field">
                  <label>Name</label>
                  <input type="text" className="input" value={draft.name || ''} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} />
                </div>
                <div className="admin-catalog-field">
                  <label>Duration (min)</label>
                  <input type="number" className="input" min={0} value={draft.duration_minutes ?? ''} onChange={(e) => setDraft((d) => ({ ...d, duration_minutes: e.target.value === '' ? 0 : Number(e.target.value) }))} />
                </div>
                <div className="admin-catalog-field admin-catalog-field--wide">
                  <label>Description</label>
                  <textarea className="input" rows={2} value={draft.description || ''} onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))} placeholder="Power and speed focus for bonus/offseason" />
                </div>
              </div>
              {[
                { key: 'warmup_exercises', label: 'Warmup', desc: 'Exercises to prepare for the drill session.' },
                { key: 'exercises', label: 'Drills', desc: 'Main drill exercises — cycled through linearly.' },
                { key: 'finisher_exercises', label: 'Finisher', desc: 'Cool-down or burnout exercises to close the session.' },
              ].map(({ key, label, desc }) => (
                <div key={key} className="admin-catalog-exercises-section">
                  <strong className="admin-catalog-exercises-title">{label}</strong>
                  <p className="text-sm text-secondary" style={{ marginBottom: 'var(--space-2)' }}>{desc}</p>
                  <table className="admin-catalog-exercises-table admin-catalog-exercises-table--drills">
                    <thead>
                      <tr>
                        <th>Exercise</th>
                        <th>Sets</th>
                        <th>Reps</th>
                        <th>Rest (s)</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {(draft[key] || []).map((ex, i) => (
                        <React.Fragment key={i}>
                          <tr>
                            <td>
                              <select
                                className="input input--sm"
                                value={ex.exercise_id || ''}
                                onChange={(e) => updateExerciseRow(key, i, 'exercise_id', e.target.value)}
                              >
                                <option value="">—</option>
                                {(exercisesList || []).map((e) => (
                                  <option key={e.id} value={e.id}>{e.name || e.id}</option>
                                ))}
                              </select>
                            </td>
                            <td><input type="number" className="input input--sm" min={0} value={ex.sets ?? ''} onChange={(e) => updateExerciseRow(key, i, 'sets', e.target.value)} /></td>
                            <td><input type="number" className="input input--sm" min={0} value={ex.reps ?? ''} onChange={(e) => updateExerciseRow(key, i, 'reps', e.target.value)} /></td>
                            <td><input type="number" className="input input--sm" min={0} value={ex.rest_seconds ?? ''} onChange={(e) => updateExerciseRow(key, i, 'rest_seconds', e.target.value)} /></td>
                            <td><button type="button" className="btn btn--ghost btn--sm" onClick={() => removeExerciseRow(key, i)} aria-label="Remove">×</button></td>
                          </tr>
                          <tr className="admin-drill-notes-row">
                            <td colSpan={5}>
                              <input type="text" className="input input--sm" value={ex.notes || ''} onChange={(e) => updateExerciseRow(key, i, 'notes', e.target.value)} placeholder="Notes" style={{ width: '100%' }} />
                            </td>
                          </tr>
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                  <button type="button" className="btn btn--secondary btn--sm" onClick={() => addExerciseRow(key)}>+ Add exercise</button>
                </div>
              ))}
            </>
          }
          footer={
            <>
              {saveError && <span style={{ color: 'var(--color-error)', fontSize: 'var(--font-size-sm)', marginRight: 'auto' }}>{saveError}</span>}
              <button type="button" className="btn btn--secondary btn--sm" onClick={closeModal} disabled={saving}>Cancel</button>
              <button type="button" className="btn btn--primary btn--sm" onClick={saveEdit} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
            </>
          }
        />
      )}
    </div>
  );
}

// ---- Finishers: name, duration, equipment_required, exercises table (exercise_id, target, sets, reps, rest_seconds, notes) ----
function AdminFinishersCatalog({ list, exercisesList, programOptions = FINISHER_PROGRAM_OPTIONS, onAfterSave }) {
  const [modalItem, setModalItem] = useState(null);
  const [isAddMode, setIsAddMode] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [programFilter, setProgramFilter] = useState('');

  const filteredList = useMemo(() => {
    const raw = list || [];
    if (!programFilter) return raw;
    if (programFilter === 'shared') return raw.filter((item) => getFinisherProgram(item) == null);
    return raw.filter((item) => getFinisherProgram(item) === programFilter);
  }, [list, programFilter]);

  const openEdit = (item) => {
    setModalItem(item);
    setIsAddMode(false);
    setSaveError(null);
    setDeleteError(null);
    setDraft({
      ...item,
      program: item?.program ?? getFinisherProgram(item) ?? 'shared',
      equipment_required: Array.isArray(item.equipment_required) ? item.equipment_required.join(', ') : '',
      exercises: Array.isArray(item.exercises) ? item.exercises.map((e) => ({ ...e })) : [],
    });
  };
  const openAddNew = () => {
    setModalItem(null);
    setIsAddMode(true);
    setSaveError(null);
    setDeleteError(null);
    setDraft({ ...EMPTY_FINISHER, program: null });
  };
  const closeModal = () => { setModalItem(null); setIsAddMode(false); setDraft(null); setSaveError(null); setDeleteError(null); };

  const handleDelete = async () => {
    const id = modalItem?.id;
    if (!id) return;
    if (!window.confirm(`Delete finisher "${modalItem?.name || id}"? This cannot be undone.`)) return;
    setDeleting(true);
    setDeleteError(null);
    setSaveError(null);
    try {
      await deleteFinisherTemplate(id);
      if (typeof onAfterSave === 'function') await onAfterSave();
      closeModal();
    } catch (err) {
      console.error('[AdminContent] Finisher delete failed:', err);
      setDeleteError(err.message || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const saveEdit = async () => {
    const id = (isAddMode ? draft?.id : (modalItem && modalItem.id)) || '';
    const docId = toSlug(id) || String(id).trim();
    if (!docId) {
      setSaveError('ID is required (e.g. my_finisher)');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const equipmentArr = typeof draft.equipment_required === 'string'
        ? draft.equipment_required.split(',').map((s) => s.trim()).filter(Boolean)
        : (Array.isArray(draft.equipment_required) ? draft.equipment_required : []);
      const payload = {
        id: docId,
        name: draft.name || '',
        program: draft.program === 'shared' ? null : (draft.program || null),
        duration_minutes: draft.duration_minutes ?? 0,
        equipment_required: equipmentArr,
        exercises: (draft.exercises || []).map((ex) => ({
          exercise_id: ex.exercise_id || '',
          target: ex.target || '',
          sets: Number(ex.sets) || 0,
          reps: Number(ex.reps) || 0,
          rest_seconds: Number(ex.rest_seconds) || 0,
          notes: ex.notes || '',
        })),
      };
      await updateFinisherTemplate(docId, payload);
      if (typeof onAfterSave === 'function') await onAfterSave();
      closeModal();
    } catch (err) {
      console.error('[AdminContent] Finisher save failed:', err);
      setSaveError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const updateExerciseRow = (index, field, value) => {
    setDraft((d) => ({
      ...d,
      exercises: (d.exercises || []).map((ex, i) =>
        i === index ? { ...ex, [field]: field === 'sets' || field === 'reps' || field === 'rest_seconds' ? (value === '' ? 0 : Number(value)) : value } : ex
      ),
    }));
  };
  const addExerciseRow = () => {
    setDraft((d) => ({ ...d, exercises: [...(d.exercises || []), { exercise_id: '', target: '', sets: 3, reps: 12, rest_seconds: 30, notes: '' }] }));
  };
  const removeExerciseRow = (index) => {
    setDraft((d) => ({ ...d, exercises: (d.exercises || []).filter((_, i) => i !== index) }));
  };

  const exerciseName = (id) => {
    const ex = (exercisesList || []).find((e) => e.id === id);
    return ex ? ex.name : id || '—';
  };

  return (
    <div className="admin-catalog admin-catalog--finishers">
      <div className="admin-content-top">
        <div className="admin-content-top-heading">
          <h2 className="admin-content-section-title">Finishers</h2>
          <p className="admin-content-section-desc">Manage finisher templates — short exercise sequences used after workouts. Edit or add templates.</p>
        </div>
        <div className="admin-content-top-actions">
          <div className="admin-catalog-filter">
            <label htmlFor="finisher-program-filter" className="admin-catalog-filter-label">Program</label>
            <select
              id="finisher-program-filter"
              className="admin-catalog-filter-select"
              value={programFilter}
              onChange={(e) => setProgramFilter(e.target.value)}
            >
              {programOptions.map(({ value, label }) => (
                <option key={value || '_all'} value={value}>{label}</option>
              ))}
            </select>
          </div>
          <button type="button" className="admin-content-add-btn" onClick={openAddNew}>
            <span className="admin-content-add-icon">+</span>
            Add new finisher
          </button>
        </div>
      </div>
      <div className="admin-catalog-list admin-catalog-list--finishers">
        {filteredList.map((item) => (
          <div key={item.id} className="admin-catalog-card admin-catalog-card--finisher">
            <div className="admin-catalog-card-body admin-catalog-card-body--full">
              <div className="admin-catalog-card-content">
                <div className="admin-catalog-card-header">
                  <strong className="admin-catalog-name">{item.name}</strong>
                  <span className="admin-catalog-id text-muted text-sm">{item.id}</span>
                  <span className="admin-catalog-program-badge" title={`Program: ${getFinisherProgram(item) ?? 'shared'}`}>
                    {getFinisherProgram(item) ?? 'shared'}
                  </span>
                </div>
                <dl className="admin-catalog-meta">
                  {item.duration_minutes != null && <><dt>Duration (min)</dt><dd>{item.duration_minutes}</dd></>}
                  {Array.isArray(item.equipment_required) && item.equipment_required.length > 0 && <><dt>Equipment</dt><dd>{item.equipment_required.join(', ')}</dd></>}
                </dl>
                {Array.isArray(item.exercises) && item.exercises.length > 0 && (
                <div className="admin-catalog-exercises-section">
                  <strong className="admin-catalog-exercises-title">Exercises & sets</strong>
                  <table className="admin-catalog-exercises-table">
                    <thead>
                      <tr>
                        <th>Exercise</th>
                        <th>Target</th>
                        <th>Sets</th>
                        <th>Reps</th>
                        <th>Rest (s)</th>
                        <th>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {item.exercises.map((ex, i) => (
                        <tr key={i}>
                          <td>{exerciseName(ex.exercise_id)}</td>
                          <td>{ex.target ?? '—'}</td>
                          <td>{ex.sets ?? '—'}</td>
                          <td>{ex.reps ?? '—'}</td>
                          <td>{ex.rest_seconds ?? '—'}</td>
                          <td>{ex.notes || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              </div>
              <div className="admin-catalog-edit-row">
                <button type="button" className="admin-catalog-edit-btn" onClick={() => openEdit(item)} aria-label="Edit">
                  <img src={EditIcon} alt="" className="admin-catalog-edit-icon" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {(modalItem || isAddMode) && draft && (
        <ContentEditModal
          title={isAddMode ? 'Add new finisher' : `Edit: ${modalItem.name}`}
          onClose={closeModal}
          body={
            <>
              <div className="admin-catalog-edit-fields">
                <div className="admin-catalog-field">
                  <label>ID</label>
                  {isAddMode ? (
                    <input type="text" className="input" value={draft.id || ''} onChange={(e) => setDraft((d) => ({ ...d, id: e.target.value }))} placeholder="e.g. my_finisher (lowercase, underscores)" />
                  ) : (
                    <input type="text" className="input" value={draft.id || ''} readOnly />
                  )}
                </div>
                <div className="admin-catalog-field">
                  <label>Name</label>
                  <input type="text" className="input" value={draft.name || ''} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} />
                </div>
                <div className="admin-catalog-field">
                  <label>Duration (min)</label>
                  <input type="number" className="input" min={0} value={draft.duration_minutes ?? ''} onChange={(e) => setDraft((d) => ({ ...d, duration_minutes: e.target.value === '' ? 0 : Number(e.target.value) }))} />
                </div>
                <div className="admin-catalog-field">
                  <label>Program</label>
                  <select
                    className="input"
                    value={draft.program ?? 'shared'}
                    onChange={(e) => setDraft((d) => ({ ...d, program: e.target.value }))}
                  >
                    {programOptions.filter((o) => o.value).map(({ value, label }) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>
                <div className="admin-catalog-field admin-catalog-field--wide">
                  <label>Equipment required (comma-sep)</label>
                  <input type="text" className="input" value={typeof draft.equipment_required === 'string' ? draft.equipment_required : (Array.isArray(draft.equipment_required) ? draft.equipment_required.join(', ') : '')} onChange={(e) => setDraft((d) => ({ ...d, equipment_required: e.target.value ? e.target.value.split(',').map((s) => s.trim()).filter(Boolean) : [] }))} />
                </div>
              </div>
              <div className="admin-catalog-exercises-section">
                <strong className="admin-catalog-exercises-title">Exercises & sets</strong>
                <table className="admin-catalog-exercises-table">
                  <thead>
                    <tr>
                      <th>Exercise</th>
                      <th>Target</th>
                      <th>Sets</th>
                      <th>Reps</th>
                      <th>Rest (s)</th>
                      <th>Notes</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(draft.exercises || []).map((ex, i) => (
                      <tr key={i}>
                        <td>
                          <select
                            className="input input--sm"
                            value={ex.exercise_id || ''}
                            onChange={(e) => updateExerciseRow(i, 'exercise_id', e.target.value)}
                          >
                            <option value="">—</option>
                            {(exercisesList || []).map((e) => (
                              <option key={e.id} value={e.id}>{e.name || e.id}</option>
                            ))}
                          </select>
                        </td>
                        <td><input type="text" className="input input--sm" value={ex.target || ''} onChange={(e) => updateExerciseRow(i, 'target', e.target.value)} placeholder="e.g. lower_back" /></td>
                        <td><input type="number" className="input input--sm" min={0} value={ex.sets ?? ''} onChange={(e) => updateExerciseRow(i, 'sets', e.target.value)} /></td>
                        <td><input type="number" className="input input--sm" min={0} value={ex.reps ?? ''} onChange={(e) => updateExerciseRow(i, 'reps', e.target.value)} /></td>
                        <td><input type="number" className="input input--sm" min={0} value={ex.rest_seconds ?? ''} onChange={(e) => updateExerciseRow(i, 'rest_seconds', e.target.value)} /></td>
                        <td><input type="text" className="input input--sm" value={ex.notes || ''} onChange={(e) => updateExerciseRow(i, 'notes', e.target.value)} placeholder="Notes" /></td>
                        <td><button type="button" className="btn btn--ghost btn--sm" onClick={() => removeExerciseRow(i)} aria-label="Remove">×</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <button type="button" className="btn btn--secondary btn--sm" onClick={addExerciseRow}>+ Add exercise</button>
              </div>
            </>
          }
          footer={
            <>
              {!isAddMode && (
                <button type="button" className="btn btn--secondary btn--sm admin-content-delete-btn" onClick={handleDelete} disabled={saving || deleting} style={{ marginRight: 'auto' }}>
                  {deleting ? 'Deleting…' : 'Delete'}
                </button>
              )}
              {(saveError || deleteError) && <span style={{ color: 'var(--color-error)', fontSize: 'var(--font-size-sm)', marginRight: 'auto' }}>{saveError || deleteError}</span>}
              <button type="button" className="btn btn--secondary btn--sm" onClick={closeModal} disabled={saving || deleting}>Cancel</button>
              <button type="button" className="btn btn--primary btn--sm" onClick={saveEdit} disabled={saving || deleting}>{saving ? 'Saving…' : 'Save'}</button>
            </>
          }
        />
      )}
    </div>
  );
}

// ---- Workouts: full workout builder (warmup + main circuits + finisher). Assign at mesocycle level. ----
const WORKOUT_PROGRAM_OPTIONS = [
  { value: '', label: 'All' },
  ...WORKOUT_EDITOR_PROGRAM_OPTIONS,
];

function getWorkoutProgram(item) {
  if (item?.program != null && item.program !== '') return item.program;
  const id = (item?.id || '').toLowerCase();
  if (id.startsWith('vb_') || id.includes('volleyball')) return 'volleyball';
  if (id.startsWith('multi_sport_') || id.includes('multi_sport')) return 'multi_sport_sprint';
  if (id.startsWith('strength_') || id.startsWith('phase_')) return null; // legacy shared
  return null; // shared — needs to be set
}

// Deduce macrocycle from workout id (multi_sport_m1_*, vb_inseason_*, vb_club_*, vb_summer_*, etc.)
const MACROCYCLE_BY_WORKOUT_ID = {
  multi_sport_m1: { id: 'multi_sport_winter_foundation', name: 'Winter Foundation Block', order: 1 },
  multi_sport_m2: { id: 'multi_sport_spring_build', name: 'Spring Build Block', order: 2 },
  multi_sport_m3: { id: 'multi_sport_summer_peak', name: 'Summer Peak Block', order: 3 },
  multi_sport_m4: { id: 'multi_sport_fall_transition', name: 'Fall Transition Block', order: 4 },
  vb_inseason: { id: 'vb_fall_inseason', name: 'Fall In-Season', order: 10 },
  vb_club_preseason: { id: 'vb_club_preseason', name: 'Club Preseason', order: 10.5 },
  vb_club: { id: 'vb_winter_spring_club', name: 'Winter-Spring Club Season', order: 11 },
  vb_summer: { id: 'vb_summer_offseason', name: 'Summer Off-Season', order: 12 },
  strength_p1: { id: 'strength_phase_1', name: 'Strength Phase 1', order: 90 },
  strength_p2: { id: 'strength_phase_2', name: 'Strength Phase 2', order: 91 },
};

function getMacrocycleForWorkout(item) {
  const id = (item?.id || '').toLowerCase();
  for (const [prefix, macro] of Object.entries(MACROCYCLE_BY_WORKOUT_ID)) {
    if (id.startsWith(prefix) || id.includes(`_${prefix}_`)) return macro;
  }
  return { id: 'other', name: 'Other', order: 99 };
}

function AdminWorkoutsCatalog({ list, warmupsList, finishersList, exercisesList, programOptions = WORKOUT_PROGRAM_OPTIONS, onAfterSave }) {
  const [modalItem, setModalItem] = useState(null);
  const [isAddMode, setIsAddMode] = useState(false);
  const [programFilter, setProgramFilter] = useState('');

  const filteredList = useMemo(() => {
    const raw = list || [];
    if (!programFilter) return raw;
    if (programFilter === 'shared') return raw.filter((item) => getWorkoutProgram(item) == null);
    return raw.filter((item) => getWorkoutProgram(item) === programFilter);
  }, [list, programFilter]);

  const workoutsByMacrocycle = useMemo(() => {
    const groups = new Map();
    for (const item of filteredList) {
      const macro = getMacrocycleForWorkout(item);
      const key = macro.id;
      if (!groups.has(key)) groups.set(key, { macro, workouts: [] });
      groups.get(key).workouts.push(item);
    }
    return Array.from(groups.values()).sort((a, b) => a.macro.order - b.macro.order);
  }, [filteredList]);

  const openEdit = (item) => {
    setModalItem(item);
    setIsAddMode(false);
  };

  const openAddNew = () => {
    setModalItem(null);
    setIsAddMode(true);
  };

  const closeModal = () => {
    setModalItem(null);
    setIsAddMode(false);
  };

  const warmupName = (id) => (warmupsList || []).find((w) => w.id === id)?.name || id || '—';
  const finisherName = (id) => (finishersList || []).find((f) => f.id === id)?.name || id || '—';

  const renderWorkoutCard = (item) => {
    const warmup = (item.blocks || []).find((b) => b.block_type === 'warmup');
    const main = (item.blocks || []).find((b) => b.block_type === 'main');
    const finisher = (item.blocks || []).find((b) => b.block_type === 'finisher');
    const circuitCount = main?.circuits?.length ?? 0;
    const program = getWorkoutProgram(item);
    const needsProgram = program == null;
    return (
      <div key={item.id} className={`admin-catalog-card admin-catalog-card--workout ${needsProgram ? 'admin-catalog-card--needs-program' : ''}`}>
        <div className="admin-catalog-card-body admin-catalog-card-body--full">
          <div className="admin-catalog-card-content">
            <div className="admin-catalog-card-header">
              <strong className="admin-catalog-name">{item.name}</strong>
              <span className="admin-catalog-id text-muted text-sm">{item.id}</span>
              {needsProgram ? (
                <span className="admin-catalog-needs-program-badge" title="Edit to set program — currently shows in All">
                  Set program
                </span>
              ) : (
                <span className="admin-catalog-program-badge" title={`Program: ${program}`}>
                  {program}
                </span>
              )}
            </div>
            <dl className="admin-catalog-meta">
              {item.estimated_duration_minutes != null && <><dt>Duration (min)</dt><dd>{item.estimated_duration_minutes}</dd></>}
              <dt>Structure</dt>
              <dd>{warmup ? warmupName(warmup.template_id) : '—'} + {circuitCount} circuit(s) + {finisher ? finisherName(finisher.template_id) : '—'}</dd>
            </dl>
          </div>
          <div className="admin-catalog-edit-row">
            <button type="button" className="admin-catalog-edit-btn" onClick={() => openEdit(item)} aria-label="Edit">
              <img src={EditIcon} alt="" className="admin-catalog-edit-icon" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="admin-catalog admin-catalog--workouts">
      <div className="admin-content-top">
        <div className="admin-content-top-heading">
          <h2 className="admin-content-section-title">Workouts</h2>
          <p className="admin-content-section-desc">Build and edit full workouts (warmup + main circuits + finisher). Assign these at the mesocycle level (Day A–F) in Programs.</p>
        </div>
        <div className="admin-content-top-actions">
          <div className="admin-catalog-filter">
            <label htmlFor="workout-program-filter" className="admin-catalog-filter-label">Program</label>
            <select
              id="workout-program-filter"
              className="admin-catalog-filter-select"
              value={programFilter}
              onChange={(e) => setProgramFilter(e.target.value)}
            >
              {programOptions.map(({ value, label }) => (
                <option key={value || '_all'} value={value}>{label}</option>
              ))}
            </select>
          </div>
          <button type="button" className="admin-content-add-btn" onClick={openAddNew}>
            <span className="admin-content-add-icon">+</span>
            Add new workout
          </button>
        </div>
      </div>
      {programFilter ? (
        <div className="admin-catalog-workouts-by-macrocycle">
          {workoutsByMacrocycle.map(({ macro, workouts }) => (
            <div key={macro.id} className="admin-catalog-macrocycle-section">
              <h3 className="admin-catalog-macrocycle-title">{macro.name}</h3>
              <div className="admin-catalog-list admin-catalog-list--workouts">
                {workouts.map((item) => renderWorkoutCard(item))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="admin-catalog-list admin-catalog-list--workouts">
          {filteredList.map((item) => renderWorkoutCard(item))}
        </div>
      )}

      {(modalItem || isAddMode) && (
        <AdminWorkoutEditorModal
          item={modalItem}
          isAddMode={isAddMode}
          warmupsList={warmupsList}
          finishersList={finishersList}
          exercisesList={exercisesList}
          defaultProgramId={programFilter || undefined}
          programOptions={programOptions.filter((o) => o.value)}
          onSave={onAfterSave}
          onClose={closeModal}
          onDelete={onAfterSave}
          showDelete={true}
        />
      )}
    </div>
  );
}

export default AdminContent;
